import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'pt' | 'en' | 'es';

interface InterpolationParams {
  [key: string]: string | number;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: InterpolationParams) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation files
const translations: Record<Language, Record<string, string>> = {
  pt: {
    // Header
    'header.enter': 'Entrar',
    'header.news': 'Novidades',
    
    // Hero
    'hero.badge': 'Muito mais que Notion/Trello e 100% brasileiro!',
    'hero.title1': 'Gerencie seus projetos e',
    'hero.title2': 'receba pagamentos',
    'hero.title3': 'em um só lugar',
    'hero.subtitle': 'Organize tarefas, colabore com clientes e receba por PIX. Tudo personalizado com sua marca.',
    'hero.cta': 'Começar Agora!',
    'hero.demo': 'Ver Demonstração',
    'hero.freeNote': 'Comece grátis — sem cartão de crédito',
    
    // Live Counter
    'counter.online': 'online agora',
    'counter.professionals': 'profissionais',
    'counter.projects': 'projetos criados',
    'counter.charges': 'em cobranças',
    
    // Offer Banner
    'offer.special': '🎉 Oferta Especial:',
    'offer.discount': '50% OFF',
    'offer.text': 'no primeiro mês do PRO!',
    'offer.cta': 'Aproveitar',
    
    // Pain Points
    'pain.title': 'Você ainda trabalha',
    'pain.title2': 'no modo caos?',
    'pain.1.pain': 'Cliente pergunta o status pelo WhatsApp',
    'pain.1.solution': 'Cliente acompanha tudo em tempo real',
    'pain.1.detail': 'Sem mais "E aí, como tá?"',
    'pain.2.pain': 'Perde horas organizando tarefas em planilhas',
    'pain.2.solution': 'Organização automática em abas e tópicos',
    'pain.2.detail': 'Seu tempo vale mais',
    'pain.3.pain': 'Não encontra arquivos e informações',
    'pain.3.solution': 'Tudo centralizado por projeto',
    'pain.3.detail': 'Acabou a bagunça',
    'pain.4.pain': 'Cobra por WhatsApp e perde controle',
    'pain.4.solution': 'Cobranças PIX com confirmação automática',
    'pain.4.detail': 'Profissionalismo que converte',
    'pain.5.pain': 'Não sabe quantos clientes tem',
    'pain.5.solution': 'Carteira de clientes organizada',
    'pain.5.detail': 'Conheça seu negócio',
    
    // What Is Section
    'whatIs.badge': 'Conheça o Taskeer',
    'whatIs.title': 'O que é o Taskeer?',
    'whatIs.description': 'O Taskeer é a plataforma completa para profissionais autônomos e prestadores de serviços digitais. Gerencie projetos, colabore com clientes e receba pagamentos via PIX — tudo em um só lugar.',
    'whatIs.description2': 'Substitua o Notion, Trello e planilhas de cobrança por uma solução 100% brasileira, feita especialmente para quem precisa de organização profissional e controle financeiro integrado.',
    'whatIs.highlight1': 'Projetos organizados',
    'whatIs.highlight2': 'Colaboração em tempo real',
    'whatIs.highlight3': 'Pagamentos PIX integrados',
    'whatIs.highlight4': 'Métricas e relatórios',

    // How It Works
    'how.title': 'Como Funciona',
    'how.subtitle': 'Veja como é simples usar o Taskeer',
    'how.step': 'Passo',
    'how.playing': 'Reproduzindo',
    'how.paused': 'Pausado',
    'how.step1.title': 'Crie seu Projeto',
    'how.step1.desc': 'Comece criando um novo projeto para seu cliente ou serviço',
    'how.step2.title': 'Organize em Abas e Tópicos',
    'how.step2.desc': 'Estruture o trabalho em abas com tópicos detalhados',
    'how.step3.title': 'Convide sua Equipe',
    'how.step3.desc': 'Adicione clientes e profissionais para colaborar',
    'how.step4.title': 'Gere Cobranças PIX',
    'how.step4.desc': 'Crie QR codes e receba pagamentos na plataforma',
    'how.step5.title': 'Acompanhe Tudo',
    'how.step5.desc': 'Dashboard com métricas de projetos e faturamento',
    
    // Stats
    'stats.badge': 'Resultados Comprovados',
    'stats.title': 'Números que Falam por Si',
    'stats.subtitle': 'Profissionais que usam o Taskeer transformam completamente sua rotina de trabalho',
    'stats.disclaimer': '* Baseado em feedback de profissionais que migraram para o Taskeer',
    'stats.1.label': 'Economizadas por semana',
    'stats.1.desc': 'Menos tempo perdido com organização manual',
    'stats.2.label': 'Mais organização',
    'stats.2.desc': 'Projetos estruturados e fáceis de acompanhar',
    'stats.3.label': 'Mais satisfação do cliente',
    'stats.3.desc': 'Transparência total no acompanhamento',
    'stats.4.label': 'Controle financeiro',
    'stats.4.desc': 'Cobranças PIX integradas e rastreáveis',
    
    // Demo
    'demo.badge': 'Veja na Prática',
    'demo.title': 'Simples, Intuitivo, Poderoso',
    'demo.subtitle': 'Veja como é fácil gerenciar seus projetos e receber pagamentos',
    'demo.cta': 'Experimente Grátis',
    'demo.topics': 'Tópicos',
    'demo.members': 'Membros',
    'demo.payments': 'Pagamentos',
    'demo.addTopic': 'Adicionar tópico',
    'demo.paymentConfirmed': 'Pagamento Confirmado!',
    'demo.receivedSuccess': 'recebido com sucesso',
    'demo.pixCharge': 'Cobrança PIX',
    'demo.scanQr': 'Escaneie o QR Code para pagar',
    'demo.topicCompleted': 'Tópico concluído',
    'demo.justNow': 'Agora mesmo',
    'demo.clientConnected': 'Cliente conectado',
    
    // Testimonials
    'testimonials.badge': 'Depoimentos',
    'testimonials.title': 'Profissionais que Transformaram seu Negócio',
    'testimonials.subtitle': 'Veja o que dizem os prestadores de serviços que já usam o Taskeer',
    'testimonials.trust': 'Junte-se a centenas de profissionais que já confiam no Taskeer',
    'testimonials.rating': '4.9/5 de satisfação',
    'testimonials.projects': '+500 projetos criados',
    'testimonials.charges': '+R$ 100k em cobranças geradas',
    
    // Features
    'features.title': 'Tudo que você precisa',
    'features.subtitle': 'Ferramentas profissionais para gerenciar seus serviços',
    'features.projectManagement': 'Gestão de Projetos',
    'features.projectManagementDesc': 'Organize projetos em abas, tópicos e descrições detalhadas',
    'features.kanban': 'Visualização Kanban',
    'features.kanbanDesc': 'Organize seus tópicos em colunas estilo Trello',
    'features.teamCollab': 'Colaboração em Equipe',
    'features.teamCollabDesc': 'Convide clientes e profissionais para trabalharem juntos',
    'features.pixCharges': 'Cobranças com PIX',
    'features.pixChargesDesc': 'Gere QR codes e gerencie pagamentos diretamente na plataforma',
    'features.budgets': 'Geração de Orçamentos',
    'features.budgetsDesc': 'Crie orçamentos profissionais personalizados para seus clientes',
    'features.finance': 'Gerenciador Financeiro',
    'features.financeDesc': 'Controle receitas, despesas e visualize relatórios completos',
    'features.myTasks': 'Meus Afazeres',
    'features.myTasksDesc': 'Visualize todas as suas tarefas em um só lugar com calendário',
    'features.clientPortal': 'Portal do Cliente',
    'features.clientPortalDesc': 'Clientes acompanham projetos e confirmam pagamentos facilmente',
    'features.achievements': 'Conquistas',
    'features.achievementsDesc': 'Gamificação com badges e conquistas para motivar sua produtividade',
    'features.dashboard': 'Dashboard Profissional',
    'features.dashboardDesc': 'Métricas de faturamento, clientes e projetos em um só lugar',
    'features.notifications': 'Notificações Push',
    'features.notificationsDesc': 'Receba alertas de prazos, pagamentos e atividades no navegador',
    'features.customization': 'Personalização Completa',
    'features.customizationDesc': 'Personalize sua plataforma com suas cores e logo',
    
    // Pricing
    'pricing.title': 'Escolha seu plano',
    'pricing.subtitle': 'Planos flexíveis para seu crescimento',
    'pricing.recommended': 'Recomendado',
    'pricing.month': '/mês',
    'pricing.prime.projects': 'Até 10 projetos',
    'pricing.prime.members': 'Até 3 membros por projeto',
    'pricing.prime.charges': 'Cobranças PIX ilimitadas',
    'pricing.prime.schedule': 'Agenda',
    'pricing.prime.budgets': 'Geração de orçamentos',
    'pricing.prime.cta': 'Começar com Prime',
    'pricing.pro.projects': 'Até 80 projetos',
    'pricing.pro.members': 'Até 10 membros por projeto',
    'pricing.pro.customization': 'Personalização avançada',
    'pricing.pro.clients': 'Carteira de clientes',
    'pricing.pro.dashboard': 'Dashboard profissional',
    'pricing.pro.billing': 'Faturamento',
    'pricing.pro.export': 'Exportações',
    'pricing.pro.andMore': 'E muito mais...',
    'pricing.pro.cta': 'Começar com PRO',
    'pricing.exclusive': 'Exclusivo',
    'pricing.noClientSub': 'Seus clientes não precisam de assinatura',
    'pricing.free.name': 'Grátis',
    'pricing.free.price': 'R$ 0',
    'pricing.free.badge': 'Para sempre',
    'pricing.free.projects': '1 projeto ativo',
    'pricing.free.client': '1 cliente convidado',
    'pricing.free.structure': '3 abas e 5 tópicos por projeto',
    'pricing.free.tools': 'Todas as ferramentas gratuitas',
    'pricing.free.noCard': 'Sem cartão de crédito',
    'pricing.free.cta': 'Criar conta grátis',
    
    // FAQ
    'faq.badge': 'Dúvidas Frequentes',
    'faq.title': 'Perguntas Frequentes',
    'faq.subtitle': 'Tudo o que você precisa saber sobre o Taskeer',
    'faq.stillQuestions': 'Ainda tem dúvidas?',
    'faq.contact': 'Entre em contato conosco',
    'faq.1.q': 'Como funciona o período de teste gratuito?',
    'faq.1.a': 'O Taskeer PRO oferece 7 dias de teste grátis após adicionar um cartão de crédito. Durante esse período, você tem acesso a todas as funcionalidades PRO. Se não quiser continuar, basta cancelar antes do término e não será cobrado.',
    'faq.2.q': 'Meus clientes precisam pagar para usar a plataforma?',
    'faq.2.a': 'Não! Seus clientes não precisam de assinatura. Eles podem acessar os projetos, acompanhar o progresso e efetuar pagamentos sem custo algum. Apenas o profissional precisa de uma conta paga.',
    'faq.3.q': 'Como funcionam as cobranças por PIX?',
    'faq.3.a': 'Você cadastra sua chave PIX uma vez no sistema. Ao criar uma cobrança, geramos automaticamente um QR Code válido para qualquer banco brasileiro. O cliente escaneia, paga, e você acompanha tudo em tempo real no Taskeer.',
    'faq.4.q': 'Posso cancelar minha assinatura a qualquer momento?',
    'faq.4.a': 'Sim! Você pode cancelar sua assinatura quando quiser. Seus projetos continuam acessíveis até o final do período pago. Após 30 dias sem renovação, os projetos são arquivados para eventual recuperação.',
    'faq.5.q': 'Quantos projetos e clientes posso ter?',
    'faq.5.a': 'No plano Prime, você pode ter até 10 projetos com 3 membros cada. No plano PRO, são até 80 projetos com 10 membros cada. Para a maioria dos freelancers, o PRO oferece espaço de sobra para crescer.',
    'faq.6.q': 'Os dados dos meus projetos são seguros?',
    'faq.6.a': 'Absolutamente! Utilizamos infraestrutura de última geração com criptografia de ponta. Seus dados e os de seus clientes são protegidos com os mais altos padrões de segurança do mercado.',
    'faq.7.q': 'Posso usar o Taskeer no celular?',
    'faq.7.a': 'Sim! O Taskeer é 100% responsivo e funciona perfeitamente em qualquer dispositivo: celular, tablet ou computador. Você pode gerenciar seus projetos de onde estiver.',
    'faq.8.q': 'Como funciona a visualização Kanban?',
    'faq.8.a': 'A visualização Kanban transforma suas abas em colunas, permitindo arrastar e soltar tópicos entre elas. É perfeito para quem gosta de visualizar o fluxo de trabalho estilo Trello, mas com toda a potência do Taskeer.',
    
    // Final CTA
    'cta.badge': 'Comece grátis, sem cartão de crédito',
    'cta.title1': 'Chega de caos.',
    'cta.title2': 'Organize seu negócio hoje.',
    'cta.subtitle': 'Junte-se a centenas de profissionais que já transformaram a forma como gerenciam projetos e recebem pagamentos. Sua nova central começa agora.',
    'cta.createAccount': 'Criar Minha Conta Grátis',
    'cta.viewPlans': 'Ver Planos e Preços',
    'cta.setup': 'Configuração em 2 minutos',
    'cta.noCommitment': 'Sem compromisso',
    'cta.support': 'Suporte em português',
    
    // Updates
    'updates.title': 'Novidades da Plataforma',
    'updates.subtitle': 'Acompanhe as últimas atualizações do Taskeer',
    'updates.viewAll': 'Ver Todas as Novidades',
    'updates.clientPortal': 'Portal do Cliente',
    'updates.clientPortalDesc': 'Área exclusiva para clientes acompanharem projetos e pagamentos.',
    'updates.pushNotifications': 'Notificações Push no Navegador',
    'updates.pushNotificationsDesc': 'Receba alertas de prazos, pagamentos e atividades importantes.',
    'updates.projectTemplates': 'Templates de Projetos',
    'updates.projectTemplatesDesc': 'Salve estruturas de projetos e reutilize em novos projetos.',
    'updates.financeManager': 'Gerenciador Financeiro Completo',
    'updates.financeManagerDesc': 'Sistema completo para controle de receitas, despesas e relatórios.',
    'updates.proDashboard': 'Dashboard Profissional',
    'updates.proDashboardDesc': 'Visualize métricas importantes do seu negócio em um só lugar.',
    
    // Footer
    'footer.about': 'Plataforma completa para prestadores de serviços digitais gerenciarem projetos e receberem pagamentos.',
    'footer.quickLinks': 'Links Rápidos',
    'footer.enter': 'Entrar',
    'footer.createAccount': 'Criar Conta',
    'footer.plans': 'Planos',
    'footer.contact': 'Contato',
    'footer.rights': 'Todos os direitos reservados.',
    
    // Language selector
    'language.pt': 'Português',
    'language.en': 'English',
    'language.es': 'Español',

    // Auth Page
    'auth.subtitle': 'Gerencie seus projetos com estilo',
    'auth.login': 'Entrar',
    'auth.signup': 'Criar Conta',
    'auth.email': 'Email',
    'auth.password': 'Senha',
    'auth.confirmPassword': 'Confirmar Senha',
    'auth.fullName': 'Nome Completo',
    'auth.country': 'País',
    'auth.phone': 'Telefone (com DDD)',
    'auth.birthDate': 'Data de Nascimento',
    'auth.loggingIn': 'Entrando...',
    'auth.creatingAccount': 'Criando...',
    'auth.backToHome': 'Voltar para início',
    'auth.loginSuccess': 'Login realizado!',
    'auth.loginSuccessDesc': 'Bem-vindo de volta ao Taskeer',
    'auth.loginError': 'Erro ao fazer login',
    'auth.signupSuccess': 'Conta registrada com sucesso!',
    'auth.signupSuccessDesc': 'Você já pode fazer login',
    'auth.signupError': 'Erro ao criar conta',
    'auth.validationError': 'Erro de validação',
    'auth.captchaRequired': 'Por favor, complete a verificação de segurança',
    'auth.captchaFailed': 'Verificação de segurança falhou. Tente novamente.',
    'auth.forgotPassword': 'Esqueci minha senha',
    'auth.forgotPasswordDesc': 'Digite seu email para receber um link de recuperação',
    'auth.sendResetLink': 'Enviar link de recuperação',
    'auth.sending': 'Enviando...',
    'auth.backToLogin': 'Voltar para login',
    'auth.resetEmailSent': 'Email enviado!',
    'auth.resetEmailSentDesc': 'Verifique sua caixa de entrada para redefinir sua senha',
    'auth.resetError': 'Erro ao enviar email',
    'auth.enterEmail': 'Digite seu email',
    'auth.newPassword': 'Nova Senha',
    'auth.confirmNewPassword': 'Confirmar Nova Senha',
    'auth.enterNewPassword': 'Digite sua nova senha abaixo',
    'auth.defineNewPassword': 'Defina sua nova senha',
    'auth.updatePassword': 'Atualizar Senha',
    'auth.updating': 'Atualizando...',
    'auth.passwordUpdated': 'Senha atualizada!',
    'auth.passwordUpdatedDesc': 'Sua senha foi alterada com sucesso. Faça login com a nova senha.',
    'auth.passwordTooShort': 'A senha deve ter no mínimo 6 caracteres',
    'auth.passwordsDoNotMatch': 'As senhas não coincidem',
    'auth.invalidResetLink': 'Link inválido ou expirado',
    'auth.invalidResetLinkDesc': 'Este link de recuperação é inválido ou já expirou. Solicite um novo link.',
    'auth.profession': 'Profissão',
    'auth.selectProfession': 'Selecione sua profissão',
    'auth.professions.freelancer': 'Freelancer / Designer / Dev',
    'auth.professions.autonomo': 'Autônomo / Prestador de Serviços',
    'auth.professions.agencia': 'Agência / Estúdio',
    'auth.professions.empresa': 'Empresa / Startup',
    'auth.professions.estudante': 'Estudante',
    'auth.professions.outro': 'Outro',
    'auth.platformInterest': 'O que você procura na plataforma?',
    'auth.selectInterest': 'Selecione seu objetivo',
    'auth.interests.gerenciarProjetos': 'Gerenciar projetos de clientes',
    'auth.interests.cobrarClientes': 'Cobrar clientes com PIX',
    'auth.interests.organizarTarefas': 'Organizar minhas tarefas',
    'auth.interests.colaborarEquipe': 'Colaborar com equipe',
    'auth.interests.controleFinanceiro': 'Controle financeiro',
    'auth.interests.tudo': 'Tudo acima!',

    // PWA Install
    'installTitle': 'Instalar Taskeer',
    'installSubtitle': 'Tenha o Taskeer sempre à mão no seu celular',
    'appInstalled': 'App já instalado!',
    'goToDashboard': 'Ir para Dashboard',
    'installOnIOS': 'Instalar no iPhone/iPad',
    'installOnAndroid': 'Instalar no Android',
    'followSteps': 'Siga os passos abaixo:',
    'tapShare': 'Toque no botão Compartilhar',
    'bottomBar': 'Na barra inferior do Safari',
    'selectAddToHome': 'Selecione "Adicionar à Tela de Início"',
    'scrollDown': 'Role a lista se necessário',
    'tapAdd': 'Toque em "Adicionar"',
    'installApp': 'Instalar App',
    'new': 'Novo',
    'installDescription': 'Acesse o Taskeer direto da tela inicial do seu celular',
    'openMenu': 'Abra o menu do navegador',
    'topRight': 'Três pontos no canto superior direito',
    'selectInstallApp': 'Selecione "Instalar app" ou "Adicionar à tela inicial"',
    'confirmInstall': 'Confirme a instalação',
    'backToHome': 'Voltar ao início',

    // Dashboard
    'dashboard.welcome': 'Seja bem vindo,',
    'dashboard.activeProjects': 'projeto(s) ativo(s)',
    'dashboard.myProjects': 'Meus Projetos',
    'dashboard.myTasks': 'Meus Afazeres',
    'dashboard.clientPortal': 'Portal do Cliente',
    'dashboard.news': 'Novidades',
    'dashboard.achievements': 'Conquistas',
    'dashboard.schedule': 'Agenda',
    'dashboard.proDashboard': 'Dashboard Profissional',
    'dashboard.billing': 'Faturamento',
    'dashboard.myClients': 'Meus Clientes',
    'dashboard.customization': 'Personalização',
    'dashboard.archived': 'Arquivados',
    'dashboard.myProfile': 'Meu Perfil',
    'dashboard.logout': 'Sair',
    'dashboard.logoutSuccess': 'Logout realizado',
    'dashboard.logoutSuccessDesc': 'Até logo!',
    'dashboard.loadError': 'Erro ao carregar projetos',
    'dashboard.notifications': 'Notificações',
    'dashboard.generateBudget': 'Gerar Orçamento',
    'dashboard.searchProjects': 'Buscar projetos...',
    'dashboard.all': 'Todos',
    'dashboard.favorites': 'Favoritos',
    'dashboard.recent': 'Recentes',
    'dashboard.noProjects': 'Nenhum projeto encontrado',
    'dashboard.noProjectsDesc': 'Crie seu primeiro projeto para começar',
    'dashboard.createFirst': 'Criar Primeiro Projeto',
    'dashboard.noFavorites': 'Nenhum favorito',
    'dashboard.noFavoritesDesc': 'Clique na estrela dos projetos para favoritá-los',
    'dashboard.noRecent': 'Nenhum projeto recente',
    'dashboard.noRecentDesc': 'Seus projetos acessados recentemente aparecerão aqui',
    'dashboard.noResults': 'Nenhum resultado',
    'dashboard.noResultsDesc': 'Tente buscar com outros termos',
    'dashboard.clearSearch': 'Limpar busca',
    'dashboard.archivePolicy': 'Projetos não renovados são arquivados após 30 dias e deletados após mais 30 dias.',
    'dashboard.unlockPotential': 'Desbloqueie o máximo potencial',
    'dashboard.customizeWithBrand': 'Personalize com sua marca',
    'dashboard.financeManagement': 'Gestão Financeira',
    'dashboard.fullFinanceControl': 'Controle completo de finanças',
    'dashboard.clientPortfolio': 'Carteira de Clientes',
    'dashboard.contactHistory': 'Histórico de todos os contatos',
    'dashboard.noLimits': 'Sem Limites',
    'dashboard.projectsMembers': '80 projetos, 10 membros',
    'dashboard.freeTrial': '7 dias grátis para testar',
    'dashboard.upgradeNow': 'Fazer Upgrade Agora',

    // Profile
    'profile.title': 'Meu Perfil',
    'profile.photo': 'Foto de Perfil',
    'profile.photoDesc': 'Sua foto será exibida em seu perfil e projetos',
    'profile.changePhoto': 'Alterar Foto',
    'profile.photoFormats': 'JPG, PNG ou WEBP. Máximo 5MB.',
    'profile.personalInfo': 'Informações Pessoais',
    'profile.personalInfoDesc': 'Atualize seus dados cadastrais',
    'profile.fullName': 'Nome Completo',
    'profile.email': 'Email',
    'profile.emailChangeNote': 'Ao alterar o email, você receberá um link de confirmação',
    'profile.phone': 'Telefone (com DDD)',
    'profile.birthDate': 'Data de Nascimento',
    'profile.pixConfig': 'Configuração PIX',
    'profile.pixConfigDesc': 'Configure seus dados PIX para receber pagamentos através da plataforma',
    'profile.pixKeyType': 'Tipo de Chave PIX',
    'profile.selectKeyType': 'Selecione o tipo de chave',
    'profile.pixKey': 'Chave PIX',
    'profile.pixBeneficiaryName': 'Nome do Beneficiário',
    'profile.pixBeneficiaryCity': 'Cidade do Beneficiário',
    'profile.saveChanges': 'Salvar Alterações',
    'profile.saving': 'Salvando...',
    'profile.changePassword': 'Alterar Senha',
    'profile.changePasswordDesc': 'Para sua segurança, escolha uma senha forte',
    'profile.currentPassword': 'Senha Atual',
    'profile.newPassword': 'Nova Senha',
    'profile.confirmPassword': 'Confirmar Nova Senha',
    'profile.updatePassword': 'Atualizar Senha',
    'profile.updating': 'Atualizando...',
    'profile.subscription': 'Assinatura',
    'profile.manageSub': 'Gerencie sua assinatura e veja os benefícios do seu plano',
    'profile.currentPlan': 'Plano Atual',
    'profile.manageSubscription': 'Gerenciar Assinatura',

    // Clients
    'clients.title': 'Carteira de Clientes',
    'clients.export': 'Exportar',
    'clients.contacts': 'Contatos',
    'clients.churnAnalysis': 'Análise de Churn',
    'clients.searchPlaceholder': 'Buscar por nome, email ou telefone...',
    'clients.all': 'Todos',
    'clients.clientsLabel': 'Clientes',
    'clients.professionals': 'Profissionais',
    'clients.totalContacts': 'Total de Contatos',
    'clients.noContactFound': 'Nenhum contato encontrado',
    'clients.noContactYet': 'Nenhum contato ainda',
    'clients.adjustSearch': 'Tente ajustar sua busca ou filtro',
    'clients.addMembersToStart': 'Adicione membros aos seus projetos para começar',
    'clients.firstContact': 'Primeiro contato',
    'clients.notProvided': 'Não informado',
    'clients.upgradeTitle': 'Carteira de Clientes',
    'clients.upgradeDesc': 'Gerencie todos os seus clientes e contatos em um único lugar. Esta funcionalidade é exclusiva para assinantes PRO.',

    // Revenue
    'revenue.title': 'Finanças',
    'revenue.subtitle': 'Gerencie suas receitas e despesas',
    'revenue.income': 'Receita',
    'revenue.expense': 'Despesa',
    'revenue.overview': 'Resumo',
    'revenue.transactions': 'Transações',
    'revenue.payments': 'Pagamentos',
    'revenue.paymentRevenue': 'Faturamento de Pagamentos',
    'revenue.approvedPayments': 'pagamento(s) aprovado(s)',
    'revenue.noApprovedPayments': 'Nenhum pagamento aprovado ainda',
    'revenue.approved': 'Aprovado',
    'revenue.project': 'Projeto',
    'revenue.paidAt': 'Pago em',
    'revenue.at': 'às',
    'revenue.edit': 'Editar',
    'revenue.totalBilling': 'Faturamento Total',
    'revenue.completeFinanceManager': 'Gerenciador de Finanças Completo',
    'revenue.financeManagerDesc': 'Controle de despesas, receitas manuais, gráficos detalhados, resumos mensais e anuais.',
    'revenue.upgradeToPro': 'Fazer upgrade para PRO',
    'revenue.upgradeTitle': 'Faturamento e Finanças',
    'revenue.upgradeDesc': 'Gerencie suas receitas, despesas e visualize relatórios financeiros completos. Esta funcionalidade é exclusiva para assinantes PRO.',

    // Project Details
    'project.back': 'Voltar',
    'project.refresh': 'Atualizar',
    'project.payments': 'Pagamentos',
    'project.archived': 'Arquivados',
    'project.members': 'Membros',
    'project.discussion': 'Discussão',
    'project.logs': 'Logs',
    'project.drawings': 'Desenhos',
    'project.saveTemplate': 'Salvar Template',
    'project.useTemplate': 'Usar Template',
    'project.deleteProject': 'Excluir Projeto',
    'project.addTab': 'Nova Aba',
    'project.addTopic': 'Novo Tópico',
    'project.noTabs': 'Nenhuma aba criada',
    'project.createFirstTab': 'Crie sua primeira aba para organizar os tópicos',
    'project.owner': 'Dono',
    'project.professional': 'Profissional',
    'project.client': 'Cliente',
    'project.addMember': 'Adicionar Membro',
    'project.loadError': 'Erro ao carregar projeto',
    'project.refreshSuccess': 'Atualizado com sucesso',
    'project.charges': 'Cobranças',
    'project.noTabsYet': 'Nenhuma aba ainda',
    'project.createFirstTabBtn': 'Criar Primeira Aba',
    'project.topicMoved': 'Tópico movido!',
    'project.movedTo': 'Movido para',
    'project.errorMovingTopic': 'Erro ao mover tópico',
    'project.errorReordering': 'Erro ao reorganizar',
    'project.couldNotSaveOrder': 'Não foi possível salvar a nova ordem.',
    'project.updateError': 'Erro ao atualizar',
    'project.couldNotUpdate': 'Não foi possível atualizar as informações',
    'project.drafts': 'Rascunhos',

    // Customization
    'customization.title': 'Personalização',
    'customization.upgradeTitle': 'Personalização',
    'customization.upgradeDesc': 'Personalize sua plataforma com suas cores, logo e wallpaper. Esta funcionalidade é exclusiva para assinantes PRO.',
    'customization.images': 'Imagens',
    'customization.imagesDesc': 'Personalize as imagens da sua marca',
    'customization.wallpaper': 'Wallpaper de Fundo',
    'customization.logo': 'Logo da Empresa',
    'customization.profilePhoto': 'Foto de Perfil',
    'customization.upload': 'Fazer Upload',
    'customization.uploadComplete': 'Upload concluído',
    'customization.wallpaperUpdated': 'Wallpaper atualizada com sucesso.',
    'customization.logoUpdated': 'Logo atualizada com sucesso.',
    'customization.profileUpdated': 'Foto de perfil atualizada com sucesso.',
    'customization.fileTooLarge': 'Arquivo muito grande',
    'customization.maxSize': 'O arquivo deve ter no máximo 5MB.',
    'customization.invalidType': 'Tipo de arquivo inválido',
    'customization.onlyImages': 'Apenas imagens são permitidas.',
    'customization.recommendedWallpaper': 'Recomendado: 1920x1080px, máximo 5MB',
    'customization.recommendedLogo': 'Recomendado: formato quadrado (500x500px), fundo transparente, máximo 5MB',
    'customization.recommendedProfile': 'Recomendado: formato quadrado (400x400px), máximo 5MB',
    'customization.colors': 'Paleta de Cores',
    'customization.colorsDesc': 'Personalize as cores da sua interface',
    'customization.primaryColor': 'Cor Primária',
    'customization.secondaryColor': 'Cor Secundária',
    'customization.accentColor': 'Cor de Destaque',
    // MyTasks
    'myTasks.title': 'Meus Afazeres',
    'myTasks.subtitle': 'Organize suas tarefas por dia',
    'myTasks.total': 'Total',
    'myTasks.pending': 'Pendentes',
    'myTasks.completed': 'Concluídos',
    'myTasks.overdue': 'Atrasados',
    'myTasks.scheduled': 'Agendados',
    'myTasks.list': 'Lista',
    'myTasks.calendar': 'Calendário',
    'myTasks.sortBy': 'Ordenar por',
    'myTasks.groupBy': 'Agrupar por',
    'myTasks.scheduledDate': 'Data Agendada',
    'myTasks.deadline': 'Prazo',
    'myTasks.startDate': 'Data de Início',
    'myTasks.priority': 'Prioridade',
    'myTasks.project': 'Projeto',
    'myTasks.byDate': 'Por Data',
    'myTasks.byProject': 'Por Projeto',
    'myTasks.byStatus': 'Por Status',
    'myTasks.noGrouping': 'Sem Agrupamento',
    'myTasks.noTasksFound': 'Nenhum afazer encontrado',
    'myTasks.notAssigned': 'Você não está atribuído a nenhum tópico.',
    'myTasks.noPending': 'Nenhum afazer pendente.',
    'myTasks.noCompleted': 'Nenhum afazer concluído.',
    'myTasks.topicDeadline': 'Prazo',
    'myTasks.scheduledFor': 'Agendado para',
    'myTasks.unscheduled': 'Afazer desagendado',
    'myTasks.scheduleError': 'Erro ao agendar',
    'myTasks.couldNotUpdate': 'Não foi possível atualizar a data.',
    'myTasks.today': 'Hoje',
    'myTasks.dragHere': 'Arraste tarefas aqui',
    'myTasks.notScheduled': 'Não Agendados',
    'myTasks.unscheduleHere': 'Arraste tarefas para cá para desagendar',
    'myTasks.more': 'mais',
    'myTasks.topic': 'Tópico',
    'myTasks.tab': 'Aba',
    'myTasks.overdueGroup': '⚠️ Atrasados',
    'myTasks.todayGroup': '📅 Hoje',
    'myTasks.tomorrowGroup': '🌅 Amanhã',
    'myTasks.thisWeekGroup': '📆 Esta Semana',
    'myTasks.thisMonthGroup': '📋 Este Mês',
    'myTasks.futureGroup': '🔮 Futuro',
    'myTasks.noDateGroup': '📝 Sem Data Definida',
    'myTasks.completedGroup': '✅ Concluídos',
    'myTasks.pendingGroup': '⏳ Pendentes',
    'myTasks.week': 'Semana',
    'myTasks.month': 'Mês',
    'myTasks.goToToday': 'Ir para Hoje',
    'myTasks.sun': 'Dom',
    'myTasks.mon': 'Seg',
    'myTasks.tue': 'Ter',
    'myTasks.wed': 'Qua',
    'myTasks.thu': 'Qui',
    'myTasks.fri': 'Sex',
    'myTasks.sat': 'Sáb',
    'myTasks.noProject': 'Sem Projeto',

    // Schedule
    'schedule.title': 'Agenda',
    'schedule.newEvent': 'Novo Evento',
    'schedule.editEvent': 'Editar Evento',
    'schedule.eventTitle': 'Título',
    'schedule.description': 'Descrição',
    'schedule.date': 'Data',
    'schedule.allDay': 'Dia inteiro',
    'schedule.start': 'Início',
    'schedule.end': 'Fim',
    'schedule.color': 'Cor',
    'schedule.purple': 'Roxo',
    'schedule.blue': 'Azul',
    'schedule.green': 'Verde',
    'schedule.yellow': 'Amarelo',
    'schedule.red': 'Vermelho',
    'schedule.pink': 'Rosa',
    'schedule.save': 'Salvar',
    'schedule.create': 'Criar',
    'schedule.cancel': 'Cancelar',
    'schedule.loadError': 'Erro ao carregar eventos',
    'schedule.eventUpdated': 'Evento atualizado!',
    'schedule.eventCreated': 'Evento criado!',
    'schedule.saveError': 'Erro ao salvar evento',
    'schedule.eventRemoved': 'Evento removido!',
    'schedule.removeError': 'Erro ao remover evento',
    'schedule.noEvents': 'Sem eventos',
    'schedule.upgradeDesc': 'Gerencie sua agenda e compromissos. Esta funcionalidade está disponível para assinantes Prime e PRO.',

    // Achievements
    'achievements.title': 'Conquistas',
    'achievements.trackProgress': 'Acompanhe seu progresso',
    'achievements.yourStreak': 'Sua Sequência',
    'achievements.yourProgress': 'Seu Progresso',
    'achievements.projects': 'Projetos',
    'achievements.tasks': 'Tarefas',
    'achievements.clients': 'Clientes',
    'achievements.payments': 'Pagamentos',
    'achievements.comments': 'Comentários',
    'achievements.allAchievements': 'Todas as Conquistas',

    // Common
    'common.loading': 'Carregando...',
    'common.save': 'Salvar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Excluir',
    'common.edit': 'Editar',
    'common.create': 'Criar',
    'common.back': 'Voltar',
    'common.close': 'Fechar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.export': 'Exportar',
    'common.required': 'Obrigatório',

    // Push Notifications
    'pushNotifications': 'Notificações Push',
    'pushEnabled': 'Notificações ativadas!',
    'pushEnabledDesc': 'Você receberá alertas mesmo com o app fechado.',
    'pushDisabled': 'Notificações desativadas',
    'pushDisabledDesc': 'Você não receberá mais alertas push.',
    'pushEnable': 'Ativar Notificações',
    'pushDisable': 'Desativar',
    'pushError': 'Erro nas notificações',
    'pushErrorDesc': 'Não foi possível configurar as notificações.',
    'pushNotSupported': 'Não suportado',
    'pushNotSupportedDesc': 'Seu navegador não suporta notificações push.',
    'pushPermissionDenied': 'Permissão negada',
    'pushPermissionDeniedDesc': 'Você pode alterar nas configurações do navegador.',

    // Dialogs
    'dialog.newProject': 'Novo Projeto',
    'dialog.createProject': 'Criar Novo Projeto',
    'dialog.createProjectDesc': 'Crie um novo projeto para organizar suas tarefas e colaborar com sua equipe.',
    'dialog.projectName': 'Nome do Projeto',
    'dialog.projectNamePlaceholder': 'Ex: Website Corporativo',
    'dialog.descriptionOptional': 'Descrição (opcional)',
    'dialog.descriptionPlaceholder': 'Descreva os objetivos e escopo do projeto...',
    'dialog.nameRequired': 'Nome obrigatório',
    'dialog.enterProjectName': 'Por favor, insira um nome para o projeto.',
    'dialog.authError': 'Erro de autenticação',
    'dialog.needLogin': 'Você precisa estar logado para criar projetos.',
    'dialog.projectCreated': 'Projeto criado!',
    'dialog.projectCreatedDesc': 'Seu projeto foi criado com sucesso.',
    'dialog.createError': 'Erro ao criar projeto',
    'dialog.createProjectBtn': 'Criar Projeto',
    
    'dialog.editProject': 'Editar Projeto',
    'dialog.editProjectDesc': 'Altere o título e a descrição do projeto.',
    'dialog.projectUpdated': 'Projeto atualizado!',
    'dialog.updateError': 'Erro ao atualizar projeto',
    
    'dialog.deleteProject': 'Excluir Projeto',
    'dialog.deleteWarning': 'Esta ação é irreversível e irá excluir permanentemente:',
    'dialog.deleteAllTabs': 'Todas as abas do projeto',
    'dialog.deleteAllTopics': 'Todos os tópicos e suas descrições',
    'dialog.deleteAllMembers': 'Todos os membros associados',
    'dialog.deleteAllCharges': 'Todas as cobranças PIX criadas',
    'dialog.typeToConfirm': 'Para confirmar, digite o nome do projeto:',
    'dialog.confirmProjectName': 'Confirmar nome do projeto',
    'dialog.typeProjectName': 'Digite o nome do projeto',
    'dialog.incorrectName': 'Nome incorreto',
    'dialog.typeExactName': 'Digite o nome exato do projeto para confirmar a exclusão.',
    'dialog.projectDeleted': 'Projeto excluído',
    'dialog.projectDeletedDesc': 'O projeto e todos os seus dados foram removidos com sucesso.',
    'dialog.deleteError': 'Erro ao excluir projeto',
    'dialog.deleting': 'Excluindo...',
    
    'dialog.leaveProject': 'Sair do Projeto',
    'dialog.leaveConfirm': 'Tem certeza que deseja sair do projeto',
    'dialog.leaveWarning': 'Você perderá acesso a todas as informações e não poderá mais colaborar neste projeto, a menos que seja convidado novamente.',
    'dialog.leftProject': 'Você saiu do projeto',
    'dialog.leftProjectDesc': 'Você não faz mais parte de',
    'dialog.leaveError': 'Erro ao sair do projeto',
    'dialog.leaving': 'Saindo...',
    
    'dialog.addMember': 'Adicionar Membro',
    'dialog.addMemberDesc': 'Adicione um membro ao projeto pelo email cadastrado na plataforma.',
    'dialog.email': 'Email',
    'dialog.emailPlaceholder': 'email@exemplo.com',
    'dialog.role': 'Cargo',
    'dialog.client': 'Cliente',
    'dialog.professional': 'Profissional',
    'dialog.validationError': 'Erro de validação',
    'dialog.userNotFound': 'Usuário não encontrado com este email',
    'dialog.alreadyMember': 'Este usuário já é membro do projeto',
    'dialog.memberAdded': 'Membro adicionado!',
    'dialog.addedAs': 'foi adicionado ao projeto como',
    'dialog.addMemberError': 'Erro ao adicionar membro',
    
    'dialog.newTab': 'Nova Aba',
    'dialog.newTabDesc': 'Crie uma nova aba para organizar seus tópicos.',
    'dialog.tabName': 'Nome da Aba',
    'dialog.tabNamePlaceholder': 'Ex: Design, Desenvolvimento, Marketing',
    'dialog.tabNameRequired': 'Por favor, insira um nome para a aba.',
    'dialog.tabCreated': 'Aba criada!',
    'dialog.tabCreatedDesc': 'A aba foi criada com sucesso.',
    'dialog.tabError': 'Erro ao criar aba',
    'dialog.createTab': 'Criar Aba',
    
    'dialog.newTopic': 'Novo Tópico',
    'dialog.newTopicDesc': 'Adicione um novo tópico para organizar suas tarefas.',
    'dialog.topicName': 'Nome do Tópico',
    'dialog.topicNamePlaceholder': 'Ex: Criar landing page',
    'dialog.topicNameRequired': 'Por favor, insira um nome para o tópico.',
    'dialog.description': 'Descrição',
    'dialog.descriptionBrief': 'Breve descrição do tópico (opcional)',
    'dialog.content': 'Conteúdo',
    'dialog.contentPlaceholder': 'Adicione notas, informações detalhadas ou qualquer conteúdo relevante...',
    'dialog.color': 'Cor',
    'dialog.topicCreated': 'Tópico criado!',
    'dialog.topicCreatedDesc': 'O tópico foi criado com sucesso.',
    'dialog.topicError': 'Erro ao criar tópico',
    'dialog.createTopic': 'Criar Tópico',
    
    'dialog.editTopic': 'Editar Tópico',
    'dialog.editTopicDesc': 'Edite o título, descrição, prazo ou mova para outra aba.',
    'dialog.title': 'Título',
    'dialog.startDate': 'Data de Início',
    'dialog.endDate': 'Data Final',
    'dialog.start': 'Início',
    'dialog.end': 'Final',
    'dialog.clear': 'Limpar',
    'dialog.responsible': 'Responsável',
    'dialog.selectResponsible': 'Selecione um responsável',
    'dialog.noResponsible': 'Sem responsável',
    'dialog.moveToTab': 'Mover para aba',
    'dialog.current': 'atual',
    'dialog.topicMoved': 'Tópico movido!',
    'dialog.topicMovedTo': 'O tópico foi movido para',
    'dialog.topicUpdated': 'Tópico atualizado!',
    'dialog.topicUpdatedDesc': 'As alterações foram salvas com sucesso.',
    'dialog.topicUpdateError': 'Erro ao atualizar tópico',
    'dialog.moveAndSave': 'Mover e Salvar',
    'dialog.saveChanges': 'Salvar Alterações',
    
    'dialog.archiveTopic': 'Arquivar tópico',
    'dialog.archiveConfirm': 'Tem certeza que deseja arquivar',
    'dialog.archiveWarning': 'O tópico será excluído automaticamente após 30 dias, mas você poderá restaurá-lo antes disso.',
    'dialog.topicArchived': 'Tópico arquivado',
    'dialog.topicArchivedDesc': 'O tópico foi arquivado e será excluído após 30 dias.',
    'dialog.archiveError': 'Erro ao arquivar tópico',
    'dialog.archive': 'Arquivar',
    'dialog.sendNotification': 'Enviar Notificação',
    'dialog.sendNotificationDesc': 'Envie uma notificação para um membro do projeto. Ela aparecerá na dashboard dele por 24 horas.',
    'dialog.selectMember': 'Selecionar membro',
    'dialog.allMembers': 'todos os membros',
    'dialog.selectMemberPlaceholder': 'Escolha um membro',
    'dialog.notificationSentAllDesc': 'Notificação enviada para todos os membros.',
    'dialog.message': 'Mensagem',
    'dialog.messagePlaceholder': 'Digite sua mensagem...',
    'dialog.notificationSent': 'Notificação enviada',
    'dialog.notificationSentDesc': 'A notificação foi enviada com sucesso.',
    'projectDetails.sendNotification': 'Enviar Notificação',
    'common.send': 'Enviar',
    'common.sending': 'Enviando...',
    
    'dialog.upgradePlan': 'Faça upgrade do seu plano',
    'dialog.limitProjects': 'Você atingiu o limite de projetos do seu plano.',
    'dialog.limitMembers': 'Você atingiu o limite de membros para este projeto.',
    'dialog.limitTabs': 'Você atingiu o limite de abas para este projeto.',
    'dialog.limitTopics': 'Você atingiu o limite de tópicos para esta aba.',
    'dialog.limitDefault': 'Você atingiu um limite do seu plano.',
    'dialog.upTo': 'Até',
    'dialog.projects': 'projetos',
    'dialog.membersPerProject': 'membros por projeto',
    'dialog.basicExport': 'Exportação básica',
    'dialog.advancedCustomization': 'Personalização avançada',
    'dialog.reportsAnalytics': 'Relatórios e analytics',
    'dialog.prioritySupport': 'Suporte prioritário',
    'dialog.advancedExport': 'Exportação avançada',
    'dialog.subscribePrime': 'Assinar Prime',
    'dialog.subscribePro': 'Assinar PRO',
    'dialog.upgradeToPro': 'Fazer Upgrade para PRO',
    'dialog.recommended': 'Recomendado',
    'dialog.processing': 'Processando...',
    'dialog.youHave': 'você tem',
    
    // ProjectDetails
    'projectDetails.errorLoading': 'Erro ao carregar projeto',
    'projectDetails.updated': 'Atualizado com sucesso',
    'projectDetails.updatedDesc': 'Todas as informações foram atualizadas',
    'projectDetails.updateError': 'Erro ao atualizar',
    'projectDetails.updateErrorDesc': 'Não foi possível atualizar as informações',
    'projectDetails.topicMoved': 'Tópico movido!',
    'projectDetails.movedTo': 'Movido para',
    'projectDetails.errorMoving': 'Erro ao mover tópico',
    'projectDetails.errorReorder': 'Erro ao reorganizar',
    'projectDetails.errorReorderDesc': 'Não foi possível salvar a nova ordem.',
    'projectDetails.refresh': 'Atualizar',
    'projectDetails.saveAsTemplate': 'Salvar como Template',
    'projectDetails.template': 'Template',
    'projectDetails.useTemplate': 'Usar Template',
    'projectDetails.deleteProject': 'Excluir Projeto',
    'projectDetails.delete': 'Excluir',
    'projectDetails.charges': 'Cobranças',
    'projectDetails.members': 'Membros',
    'projectDetails.archived': 'Arquivados',
    'projectDetails.drawings': 'Rascunhos',
    'projectDetails.discussion': 'Discussão',
    'projectDetails.logs': 'Logs',
    'projectDetails.newTab': 'Nova Aba',
    'projectDetails.tabs': 'Abas',
    'projectDetails.limit': 'Limite',
    'projectDetails.noTabsYet': 'Nenhuma aba ainda',
    'projectDetails.noTabsDesc': 'Crie sua primeira aba para começar a organizar os tópicos do projeto.',
    'projectDetails.createFirstTab': 'Criar Primeira Aba',
    'projectDetails.projectMembers': 'Membros do Projeto',
    'projectDetails.addMember': 'Adicionar Membro',
    'projectDetails.projectDiscussion': 'Discussão do Projeto',
    'projectDetails.tabLimitReached': 'Limite de tópicos atingido',
    'projectDetails.tabLimitReachedDesc': 'Esta aba já atingiu o limite máximo de tópicos.',
    
    // Board/Topic Components
    'board.removePriority': 'Remover prioridade',
    'board.markAsPriority': 'Marcar como prioridade',
    'board.settings': 'Configurações',
    'board.archive': 'Arquivar',
    'board.edit': 'Editar',
    'board.deadline': 'Prazo',
    'board.delete': 'Excluir',
    'board.dropHere': 'Solte aqui',
    'board.noTopics': 'Nenhum tópico',
    'board.add': 'Adicionar',
    'board.topicMoved': 'Tópico movido!',
    'board.movedTo': 'Movido para',
    'board.errorMoving': 'Erro ao mover',
    'board.errorUpdatingTopic': 'Erro ao atualizar tópico',
    'board.errorUpdatingPriority': 'Erro ao atualizar prioridade',
    'board.tabLimitReached': 'Limite de tópicos atingido',
    'board.tabLimitReachedDesc': 'Esta aba já atingiu o limite máximo de tópicos.',
    
    // Members List
    'members.roleUpdated': 'Cargo atualizado',
    'members.roleUpdatedDesc': 'O cargo do membro foi alterado com sucesso.',
    'members.errorUpdatingRole': 'Erro ao atualizar cargo',
    'members.memberRemoved': 'Membro removido',
    'members.memberRemovedDesc': 'O membro foi removido do projeto.',
    'members.errorRemoving': 'Erro ao remover membro',
    'members.professional': 'Profissional',
    'members.client': 'Cliente',
    'members.addedOn': 'Adicionado em',
    'members.noMembers': 'Nenhum membro adicionado ainda.',
    'members.removeMember': 'Remover membro',
    'members.removeConfirm': 'Tem certeza que deseja remover',
    'members.removeWarning': 'do projeto? Esta ação não pode ser desfeita.',
    'members.remove': 'Remover',

    // Forms
    'dashboard.forms': 'Formulários',
    'forms.title': 'Formulários',
    'forms.subtitle': 'Crie formulários para coletar informações',
    'forms.create': 'Criar Formulário',
    'forms.createTitle': 'Novo Formulário',
    'forms.createDescription': 'Crie um formulário para enviar para pessoas preencherem.',
    'forms.formTitle': 'Título',
    'forms.formTitlePlaceholder': 'Ex: Briefing do Projeto',
    'forms.formDescription': 'Descrição',
    'forms.formDescriptionPlaceholder': 'Descreva o objetivo do formulário',
    'forms.createAndEdit': 'Criar e Editar',
    'forms.titleRequired': 'O título é obrigatório',
    'forms.created': 'Formulário criado!',
    'forms.createError': 'Erro ao criar formulário',
    'forms.deleted': 'Formulário excluído',
    'forms.deleteError': 'Erro ao excluir',
    'forms.deleteTitle': 'Excluir formulário?',
    'forms.deleteDescription': 'Esta ação não pode ser desfeita. Todas as respostas serão perdidas.',
    'forms.linkCopied': 'Link copiado!',
    'forms.questions': 'perguntas',
    'forms.responses': 'Respostas',
    'forms.response': 'Resposta',
    'forms.viewResponses': 'Ver Respostas',
    'forms.active': 'Ativo',
    'forms.inactive': 'Inativo',
    'forms.expired': 'Expirado',
    'forms.expiresIn': 'Expira em {time}',
    'forms.expiredAt': 'Expirou em {date}',
    'forms.edit': 'Editar',
    'forms.empty': 'Nenhum formulário',
    'forms.emptyDescription': 'Crie formulários para coletar informações de clientes.',
    'forms.createFirst': 'Criar Primeiro Formulário',
    'forms.upgradeTitle': 'Formulários',
    'forms.upgradeDescription': 'Crie formulários personalizados para coletar informações. Esta funcionalidade é exclusiva para assinantes PRIME e PRO.',
    'forms.backToForms': 'Voltar para Formulários',
    'forms.copyLink': 'Copiar Link',
    'forms.preview': 'Visualizar',
    'forms.formSettings': 'Configurações do Formulário',
    'forms.formActive': 'Formulário ativo',
    'forms.addQuestion': 'Adicionar Pergunta',
    'forms.addFirstQuestion': 'Adicionar Primeira Pergunta',
    'forms.noQuestions': 'Nenhuma pergunta adicionada',
    'forms.maxQuestions': 'Limite de 30 perguntas atingido',
    'forms.questionText': 'Pergunta',
    'forms.questionTextPlaceholder': 'Digite sua pergunta',
    'forms.questionType': 'Tipo',
    'forms.required': 'Obrigatório',
    'forms.options': 'Opções',
    'forms.optionsPlaceholder': 'Uma opção por linha',
    'forms.optionsHint': 'Digite uma opção por linha',
    'forms.saved': 'Formulário salvo!',
    'forms.saveError': 'Erro ao salvar',
    'forms.notFound': 'Formulário não encontrado',
    'forms.questionTypes.text': 'Texto curto',
    'forms.questionTypes.textarea': 'Texto longo',
    'forms.questionTypes.number': 'Número',
    'forms.questionTypes.email': 'Email',
    'forms.questionTypes.phone': 'Telefone',
    'forms.questionTypes.date': 'Data',
    'forms.questionTypes.select': 'Seleção',
    'forms.questionTypes.radio': 'Escolha única',
    'forms.questionTypes.checkbox': 'Múltipla escolha',
    'forms.formNotAvailable': 'Formulário indisponível',
    'forms.formNotAvailableDescription': 'Este formulário não existe ou já expirou.',
    'forms.thankYou': 'Obrigado!',
    'forms.responseRecorded': 'Sua resposta foi registrada com sucesso.',
    'forms.yourName': 'Seu nome',
    'forms.yourNamePlaceholder': 'Digite seu nome',
    'forms.yourEmail': 'Seu email',
    'forms.yourEmailPlaceholder': 'Digite seu email',
    'forms.yourPhone': 'Seu telefone',
    'forms.yourPhonePlaceholder': 'Digite seu telefone',
    'forms.typeAnswer': 'Digite sua resposta',
    'forms.typeNumber': 'Digite um número',
    'forms.typeEmail': 'Digite um email',
    'forms.typePhone': 'Digite um telefone',
    'forms.selectOption': 'Selecione uma opção',
    'forms.submit': 'Enviar Resposta',
    'forms.submitting': 'Enviando...',
    'forms.submitError': 'Erro ao enviar resposta',
    'forms.requiredField': 'O campo "{field}" é obrigatório',
    'forms.noResponses': 'Nenhuma resposta ainda',
    'forms.noAnswers': 'Nenhuma resposta registrada',
    'forms.unknownQuestion': 'Pergunta removida',
    'forms.submittedAt': 'Enviado em',
    'forms.exportSuccess': 'Exportação concluída!',

    // Subscription Page
    'subscription.choosePlan': 'Escolha seu plano',
    'subscription.unlockPotential': 'Desbloqueie todo o potencial do Taskeer',
    'subscription.currentPlan': 'Plano atual',
    'subscription.recommended': 'Recomendado',
    'subscription.daysFree': 'dias grátis',
    'subscription.yourPlan': 'Seu Plano',
    'subscription.month': 'mês',
    'subscription.freeTrial': 'Teste grátis de 7 dias',
    'subscription.currentPlanBtn': 'Plano Atual',
    'subscription.manage': 'Gerenciar',
    'subscription.downgrade': 'Fazer Downgrade',
    'subscription.basicPlan': 'Plano Básico',
    'subscription.processing': 'Processando...',
    'subscription.startTrial': 'Iniciar Trial de {days} dias',
    'subscription.subscribeNow': 'Assinar Agora',
    'subscription.upgrade': 'Fazer Upgrade',
    'subscription.startingFrom': 'A partir de',
    'subscription.upgradeToPrime': 'Fazer Upgrade',
    'subscription.back': 'Voltar',
    'subscription.primeOrPro': 'Taskeer Prime ou PRO',
    'subscription.exclusiveFeatures': 'Acesso a funcionalidades exclusivas',
    'subscription.trialInfo': 'O plano PRO inclui 7 dias de teste grátis. Após o período de teste, será cobrado automaticamente ou você pode cancelar a qualquer momento.',
    'subscription.backToDashboard': 'Voltar ao Dashboard',
    'subscription.free': 'Gratuito',
    'subscription.feature1Project': '1 projeto',
    'subscription.feature1Member': '1 membro por projeto',
    'subscription.feature3Tabs': '3 abas por projeto',
    'subscription.feature5Topics': '5 tópicos por aba',
    'subscription.featureBasic': 'Funcionalidades básicas',
    'subscription.feature10Projects': 'Até 10 projetos',
    'subscription.feature3Members': 'Até 3 membros por projeto',
    'subscription.feature20Tabs': '20 abas por projeto',
    'subscription.feature30Topics': '30 tópicos por aba',
    'subscription.featureExportCSV': 'Exportação básica (CSV)',
    'subscription.featureEmailSupport': 'Suporte por email',
    'subscription.feature80Projects': 'Até 80 projetos',
    'subscription.feature10Members': 'Até 10 membros por projeto',
    'subscription.featureCustomization': 'Personalização avançada',
    'subscription.featureAnalytics': 'Relatórios e analytics',
    'subscription.featureClients': 'Carteira de clientes',
    'subscription.featureExportAdvanced': 'Exportação avançada (Excel, PDF)',
  },
  en: {
    // Header
    'header.enter': 'Sign In',
    'header.news': 'News',
    
    // Hero
    'hero.badge': 'Much more than Notion/Trello and 100% Brazilian!',
    'hero.title1': 'Manage your projects and',
    'hero.title2': 'receive payments',
    'hero.title3': 'in one place',
    'hero.subtitle': 'Organize tasks, collaborate with clients and receive via PIX. All customized with your brand.',
    'hero.cta': 'Get Started!',
    'hero.demo': 'Watch Demo',
    'hero.freeNote': 'Start free — no credit card required',
    
    // Live Counter
    'counter.online': 'online now',
    'counter.professionals': 'professionals',
    'counter.projects': 'projects created',
    'counter.charges': 'in charges',
    
    // Offer Banner
    'offer.special': '🎉 Special Offer:',
    'offer.discount': '50% OFF',
    'offer.text': 'on the first month of PRO!',
    'offer.cta': 'Get it now',
    
    // Pain Points
    'pain.title': 'Are you still working',
    'pain.title2': 'in chaos mode?',
    'pain.1.pain': 'Client asks for status via WhatsApp',
    'pain.1.solution': 'Client tracks everything in real-time',
    'pain.1.detail': 'No more "How\'s it going?"',
    'pain.2.pain': 'Waste hours organizing tasks in spreadsheets',
    'pain.2.solution': 'Automatic organization in tabs and topics',
    'pain.2.detail': 'Your time is worth more',
    'pain.3.pain': 'Can\'t find files and information',
    'pain.3.solution': 'Everything centralized by project',
    'pain.3.detail': 'No more mess',
    'pain.4.pain': 'Charge via WhatsApp and lose control',
    'pain.4.solution': 'PIX charges with automatic confirmation',
    'pain.4.detail': 'Professionalism that converts',
    'pain.5.pain': 'Don\'t know how many clients you have',
    'pain.5.solution': 'Organized client portfolio',
    'pain.5.detail': 'Know your business',
    
    // What Is Section
    'whatIs.badge': 'Meet Taskeer',
    'whatIs.title': 'What is Taskeer?',
    'whatIs.description': 'Taskeer is the complete platform for freelancers and digital service providers. Manage projects, collaborate with clients and receive payments via PIX — all in one place.',
    'whatIs.description2': 'Replace Notion, Trello and billing spreadsheets with a 100% Brazilian solution, specially made for those who need professional organization and integrated financial control.',
    'whatIs.highlight1': 'Organized projects',
    'whatIs.highlight2': 'Real-time collaboration',
    'whatIs.highlight3': 'Integrated PIX payments',
    'whatIs.highlight4': 'Metrics and reports',

    // How It Works
    'how.title': 'How It Works',
    'how.subtitle': 'See how simple it is to use Taskeer',
    'how.step': 'Step',
    'how.playing': 'Playing',
    'how.paused': 'Paused',
    'how.step1.title': 'Create Your Project',
    'how.step1.desc': 'Start by creating a new project for your client or service',
    'how.step2.title': 'Organize in Tabs and Topics',
    'how.step2.desc': 'Structure work in tabs with detailed topics',
    'how.step3.title': 'Invite Your Team',
    'how.step3.desc': 'Add clients and professionals to collaborate',
    'how.step4.title': 'Generate PIX Charges',
    'how.step4.desc': 'Create QR codes and receive payments on the platform',
    'how.step5.title': 'Track Everything',
    'how.step5.desc': 'Dashboard with project and billing metrics',
    
    // Stats
    'stats.badge': 'Proven Results',
    'stats.title': 'Numbers That Speak for Themselves',
    'stats.subtitle': 'Professionals who use Taskeer completely transform their work routine',
    'stats.disclaimer': '* Based on feedback from professionals who migrated to Taskeer',
    'stats.1.label': 'Saved per week',
    'stats.1.desc': 'Less time wasted with manual organization',
    'stats.2.label': 'More organization',
    'stats.2.desc': 'Structured projects easy to follow',
    'stats.3.label': 'More client satisfaction',
    'stats.3.desc': 'Total transparency in tracking',
    'stats.4.label': 'Financial control',
    'stats.4.desc': 'Integrated and trackable PIX charges',
    
    // Demo
    'demo.badge': 'See it in Practice',
    'demo.title': 'Simple, Intuitive, Powerful',
    'demo.subtitle': 'See how easy it is to manage your projects and receive payments',
    'demo.cta': 'Try it Free',
    'demo.topics': 'Topics',
    'demo.members': 'Members',
    'demo.payments': 'Payments',
    'demo.addTopic': 'Add topic',
    'demo.paymentConfirmed': 'Payment Confirmed!',
    'demo.receivedSuccess': 'received successfully',
    'demo.pixCharge': 'PIX Charge',
    'demo.scanQr': 'Scan QR Code to pay',
    'demo.topicCompleted': 'Topic completed',
    'demo.justNow': 'Just now',
    'demo.clientConnected': 'Client connected',
    
    // Testimonials
    'testimonials.badge': 'Testimonials',
    'testimonials.title': 'Professionals Who Transformed Their Business',
    'testimonials.subtitle': 'See what service providers using Taskeer have to say',
    'testimonials.trust': 'Join hundreds of professionals who already trust Taskeer',
    'testimonials.rating': '4.9/5 satisfaction',
    'testimonials.projects': '+500 projects created',
    'testimonials.charges': '+R$ 100k in charges generated',
    
    // Features
    'features.title': 'Everything you need',
    'features.subtitle': 'Professional tools to manage your services',
    'features.projectManagement': 'Project Management',
    'features.projectManagementDesc': 'Organize projects in tabs, topics and detailed descriptions',
    'features.kanban': 'Kanban View',
    'features.kanbanDesc': 'Organize your topics in Trello-style columns',
    'features.teamCollab': 'Team Collaboration',
    'features.teamCollabDesc': 'Invite clients and professionals to work together',
    'features.pixCharges': 'PIX Payments',
    'features.pixChargesDesc': 'Generate QR codes and manage payments directly on the platform',
    'features.budgets': 'Budget Generation',
    'features.budgetsDesc': 'Create professional customized budgets for your clients',
    'features.finance': 'Financial Manager',
    'features.financeDesc': 'Control income, expenses and view complete reports',
    'features.myTasks': 'My Tasks',
    'features.myTasksDesc': 'View all your tasks in one place with calendar',
    'features.clientPortal': 'Client Portal',
    'features.clientPortalDesc': 'Clients can track projects and confirm payments easily',
    'features.achievements': 'Achievements',
    'features.achievementsDesc': 'Gamification with badges and achievements to boost productivity',
    'features.dashboard': 'Professional Dashboard',
    'features.dashboardDesc': 'Billing, clients and projects metrics in one place',
    'features.notifications': 'Push Notifications',
    'features.notificationsDesc': 'Receive deadline, payment and activity alerts in your browser',
    'features.customization': 'Full Customization',
    'features.customizationDesc': 'Customize your platform with your colors and logo',
    
    // Pricing
    'pricing.title': 'Choose your plan',
    'pricing.subtitle': 'Flexible plans for your growth',
    'pricing.recommended': 'Recommended',
    'pricing.month': '/month',
    'pricing.prime.projects': 'Up to 10 projects',
    'pricing.prime.members': 'Up to 3 members per project',
    'pricing.prime.charges': 'Unlimited PIX charges',
    'pricing.prime.schedule': 'Schedule',
    'pricing.prime.budgets': 'Budget generation',
    'pricing.prime.cta': 'Start with Prime',
    'pricing.pro.projects': 'Up to 80 projects',
    'pricing.pro.members': 'Up to 10 members per project',
    'pricing.pro.customization': 'Advanced customization',
    'pricing.pro.clients': 'Client portfolio',
    'pricing.pro.dashboard': 'Professional dashboard',
    'pricing.pro.billing': 'Billing',
    'pricing.pro.export': 'Exports',
    'pricing.pro.andMore': 'And much more...',
    'pricing.pro.cta': 'Start with PRO',
    'pricing.exclusive': 'Exclusive',
    'pricing.noClientSub': 'Your clients don\'t need a subscription',
    'pricing.free.name': 'Free',
    'pricing.free.price': '$0',
    'pricing.free.badge': 'Forever',
    'pricing.free.projects': '1 active project',
    'pricing.free.client': '1 invited client',
    'pricing.free.structure': '3 tabs and 5 topics per project',
    'pricing.free.tools': 'All free tools',
    'pricing.free.noCard': 'No credit card',
    'pricing.free.cta': 'Create free account',
    
    // FAQ
    'faq.badge': 'FAQ',
    'faq.title': 'Frequently Asked Questions',
    'faq.subtitle': 'Everything you need to know about Taskeer',
    'faq.stillQuestions': 'Still have questions?',
    'faq.contact': 'Contact us',
    'faq.1.q': 'How does the free trial work?',
    'faq.1.a': 'Taskeer PRO offers 7 days free trial after adding a credit card. During this period, you have access to all PRO features. If you don\'t want to continue, just cancel before it ends and you won\'t be charged.',
    'faq.2.q': 'Do my clients need to pay to use the platform?',
    'faq.2.a': 'No! Your clients don\'t need a subscription. They can access projects, track progress and make payments at no cost. Only the professional needs a paid account.',
    'faq.3.q': 'How do PIX charges work?',
    'faq.3.a': 'You register your PIX key once in the system. When creating a charge, we automatically generate a valid QR Code for any Brazilian bank. The client scans, pays, and you track everything in real-time on Taskeer.',
    'faq.4.q': 'Can I cancel my subscription at any time?',
    'faq.4.a': 'Yes! You can cancel your subscription whenever you want. Your projects remain accessible until the end of the paid period. After 30 days without renewal, projects are archived for eventual recovery.',
    'faq.5.q': 'How many projects and clients can I have?',
    'faq.5.a': 'On the Prime plan, you can have up to 10 projects with 3 members each. On the PRO plan, up to 80 projects with 10 members each. For most freelancers, PRO offers plenty of room to grow.',
    'faq.6.q': 'Is my project data secure?',
    'faq.6.a': 'Absolutely! We use state-of-the-art infrastructure with end-to-end encryption. Your data and your clients\' are protected with the highest security standards in the market.',
    'faq.7.q': 'Can I use Taskeer on mobile?',
    'faq.7.a': 'Yes! Taskeer is 100% responsive and works perfectly on any device: phone, tablet or computer. You can manage your projects from anywhere.',
    'faq.8.q': 'How does the Kanban view work?',
    'faq.8.a': 'The Kanban view turns your tabs into columns, allowing you to drag and drop topics between them. Perfect for those who like to visualize the workflow Trello-style, but with all of Taskeer\'s power.',
    
    // Final CTA
    'cta.badge': 'Start free, no credit card required',
    'cta.title1': 'No more chaos.',
    'cta.title2': 'Organize your business today.',
    'cta.subtitle': 'Join hundreds of professionals who have already transformed how they manage projects and receive payments. Your new central hub starts now.',
    'cta.createAccount': 'Create My Free Account',
    'cta.viewPlans': 'View Plans and Pricing',
    'cta.setup': '2-minute setup',
    'cta.noCommitment': 'No commitment',
    'cta.support': 'Support in Portuguese',
    
    // Updates
    'updates.title': 'Platform Updates',
    'updates.subtitle': 'Follow the latest Taskeer updates',
    'updates.viewAll': 'View All Updates',
    'updates.clientPortal': 'Client Portal',
    'updates.clientPortalDesc': 'Exclusive area for clients to track projects and payments.',
    'updates.pushNotifications': 'Browser Push Notifications',
    'updates.pushNotificationsDesc': 'Receive deadline, payment and important activity alerts.',
    'updates.projectTemplates': 'Project Templates',
    'updates.projectTemplatesDesc': 'Save project structures and reuse in new projects.',
    'updates.financeManager': 'Complete Finance Manager',
    'updates.financeManagerDesc': 'Complete system for income, expense and report control.',
    'updates.proDashboard': 'Professional Dashboard',
    'updates.proDashboardDesc': 'View important metrics of your business in one place.',
    
    // Footer
    'footer.about': 'Complete platform for digital service providers to manage projects and receive payments.',
    'footer.quickLinks': 'Quick Links',
    'footer.enter': 'Sign In',
    'footer.createAccount': 'Create Account',
    'footer.plans': 'Plans',
    'footer.contact': 'Contact',
    'footer.rights': 'All rights reserved.',
    
    // Language selector
    'language.pt': 'Português',
    'language.en': 'English',
    'language.es': 'Español',

    // Auth Page
    'auth.subtitle': 'Manage your projects with style',
    'auth.login': 'Sign In',
    'auth.signup': 'Create Account',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirmPassword': 'Confirm Password',
    'auth.fullName': 'Full Name',
    'auth.country': 'Country',
    'auth.phone': 'Phone (with area code)',
    'auth.birthDate': 'Birth Date',
    'auth.loggingIn': 'Signing in...',
    'auth.creatingAccount': 'Creating...',
    'auth.backToHome': 'Back to home',
    'auth.loginSuccess': 'Login successful!',
    'auth.loginSuccessDesc': 'Welcome back to Taskeer',
    'auth.loginError': 'Login error',
    'auth.signupSuccess': 'Account created successfully!',
    'auth.signupSuccessDesc': 'You can now log in',
    'auth.signupError': 'Error creating account',
    'auth.validationError': 'Validation error',
    'auth.captchaRequired': 'Please complete the security verification',
    'auth.captchaFailed': 'Security verification failed. Please try again.',
    'auth.forgotPassword': 'Forgot my password',
    'auth.forgotPasswordDesc': 'Enter your email to receive a recovery link',
    'auth.sendResetLink': 'Send recovery link',
    'auth.sending': 'Sending...',
    'auth.backToLogin': 'Back to login',
    'auth.resetEmailSent': 'Email sent!',
    'auth.resetEmailSentDesc': 'Check your inbox to reset your password',
    'auth.resetError': 'Error sending email',
    'auth.enterEmail': 'Enter your email',
    'auth.newPassword': 'New Password',
    'auth.confirmNewPassword': 'Confirm New Password',
    'auth.enterNewPassword': 'Enter your new password below',
    'auth.defineNewPassword': 'Set your new password',
    'auth.updatePassword': 'Update Password',
    'auth.updating': 'Updating...',
    'auth.passwordUpdated': 'Password updated!',
    'auth.passwordUpdatedDesc': 'Your password has been changed successfully. Log in with your new password.',
    'auth.passwordTooShort': 'Password must be at least 6 characters',
    'auth.passwordsDoNotMatch': 'Passwords do not match',
    'auth.invalidResetLink': 'Invalid or expired link',
    'auth.invalidResetLinkDesc': 'This recovery link is invalid or has expired. Request a new link.',
    'auth.profession': 'Profession',
    'auth.selectProfession': 'Select your profession',
    'auth.professions.freelancer': 'Freelancer / Designer / Dev',
    'auth.professions.autonomo': 'Self-employed / Service Provider',
    'auth.professions.agencia': 'Agency / Studio',
    'auth.professions.empresa': 'Company / Startup',
    'auth.professions.estudante': 'Student',
    'auth.professions.outro': 'Other',
    'auth.platformInterest': 'What are you looking for on the platform?',
    'auth.selectInterest': 'Select your goal',
    'auth.interests.gerenciarProjetos': 'Manage client projects',
    'auth.interests.cobrarClientes': 'Charge clients with PIX',
    'auth.interests.organizarTarefas': 'Organize my tasks',
    'auth.interests.colaborarEquipe': 'Collaborate with team',
    'auth.interests.controleFinanceiro': 'Financial control',
    'auth.interests.tudo': 'All of the above!',

    // PWA Install
    'installTitle': 'Install Taskeer',
    'installSubtitle': 'Have Taskeer always at hand on your phone',
    'appInstalled': 'App already installed!',
    'goToDashboard': 'Go to Dashboard',
    'installOnIOS': 'Install on iPhone/iPad',
    'installOnAndroid': 'Install on Android',
    'followSteps': 'Follow the steps below:',
    'tapShare': 'Tap the Share button',
    'bottomBar': 'In Safari\'s bottom bar',
    'selectAddToHome': 'Select "Add to Home Screen"',
    'scrollDown': 'Scroll the list if needed',
    'tapAdd': 'Tap "Add"',
    'installApp': 'Install App',
    'new': 'New',
    'installDescription': 'Access Taskeer directly from your phone\'s home screen',
    'openMenu': 'Open browser menu',
    'topRight': 'Three dots in top right corner',
    'selectInstallApp': 'Select "Install app" or "Add to Home screen"',
    'confirmInstall': 'Confirm installation',
    'backToHome': 'Back to home',

    // Dashboard
    'dashboard.welcome': 'Welcome,',
    'dashboard.activeProjects': 'active project(s)',
    'dashboard.myProjects': 'My Projects',
    'dashboard.myTasks': 'My Tasks',
    'dashboard.clientPortal': 'Client Portal',
    'dashboard.news': 'News',
    'dashboard.achievements': 'Achievements',
    'dashboard.schedule': 'Schedule',
    'dashboard.proDashboard': 'Professional Dashboard',
    'dashboard.billing': 'Billing',
    'dashboard.myClients': 'My Clients',
    'dashboard.customization': 'Customization',
    'dashboard.archived': 'Archived',
    'dashboard.myProfile': 'My Profile',
    'dashboard.logout': 'Logout',
    'dashboard.logoutSuccess': 'Logged out',
    'dashboard.logoutSuccessDesc': 'See you!',
    'dashboard.loadError': 'Error loading projects',
    'dashboard.notifications': 'Notifications',
    'dashboard.generateBudget': 'Generate Budget',
    'dashboard.searchProjects': 'Search projects...',
    'dashboard.all': 'All',
    'dashboard.favorites': 'Favorites',
    'dashboard.recent': 'Recent',
    'dashboard.noProjects': 'No projects found',
    'dashboard.noProjectsDesc': 'Create your first project to get started',
    'dashboard.createFirst': 'Create First Project',
    'dashboard.noFavorites': 'No favorites',
    'dashboard.noFavoritesDesc': 'Click the star on projects to favorite them',
    'dashboard.noRecent': 'No recent projects',
    'dashboard.noRecentDesc': 'Your recently accessed projects will appear here',
    'dashboard.noResults': 'No results',
    'dashboard.noResultsDesc': 'Try searching with different terms',
    'dashboard.clearSearch': 'Clear search',
    'dashboard.archivePolicy': 'Unrewned projects are archived after 30 days and deleted after another 30 days.',
    'dashboard.unlockPotential': 'Unlock your full potential',
    'dashboard.customizeWithBrand': 'Customize with your brand',
    'dashboard.financeManagement': 'Finance Management',
    'dashboard.fullFinanceControl': 'Complete finance control',
    'dashboard.clientPortfolio': 'Client Portfolio',
    'dashboard.contactHistory': 'All contacts history',
    'dashboard.noLimits': 'No Limits',
    'dashboard.projectsMembers': '80 projects, 10 members',
    'dashboard.freeTrial': '7-day free trial',
    'dashboard.upgradeNow': 'Upgrade Now',

    // Profile
    'profile.title': 'My Profile',
    'profile.photo': 'Profile Photo',
    'profile.photoDesc': 'Your photo will be displayed on your profile and projects',
    'profile.changePhoto': 'Change Photo',
    'profile.photoFormats': 'JPG, PNG or WEBP. Max 5MB.',
    'profile.personalInfo': 'Personal Information',
    'profile.personalInfoDesc': 'Update your profile data',
    'profile.fullName': 'Full Name',
    'profile.email': 'Email',
    'profile.emailChangeNote': 'When changing email, you will receive a confirmation link',
    'profile.phone': 'Phone (with area code)',
    'profile.birthDate': 'Birth Date',
    'profile.pixConfig': 'PIX Configuration',
    'profile.pixConfigDesc': 'Configure your PIX data to receive payments through the platform',
    'profile.pixKeyType': 'PIX Key Type',
    'profile.selectKeyType': 'Select key type',
    'profile.pixKey': 'PIX Key',
    'profile.pixBeneficiaryName': 'Beneficiary Name',
    'profile.pixBeneficiaryCity': 'Beneficiary City',
    'profile.saveChanges': 'Save Changes',
    'profile.saving': 'Saving...',
    'profile.changePassword': 'Change Password',
    'profile.changePasswordDesc': 'For your security, choose a strong password',
    'profile.currentPassword': 'Current Password',
    'profile.newPassword': 'New Password',
    'profile.confirmPassword': 'Confirm New Password',
    'profile.updatePassword': 'Update Password',
    'profile.updating': 'Updating...',
    'profile.subscription': 'Subscription',
    'profile.manageSub': 'Manage your subscription and see your plan benefits',
    'profile.currentPlan': 'Current Plan',
    'profile.manageSubscription': 'Manage Subscription',

    // Clients
    'clients.title': 'Client Portfolio',
    'clients.export': 'Export',
    'clients.contacts': 'Contacts',
    'clients.churnAnalysis': 'Churn Analysis',
    'clients.searchPlaceholder': 'Search by name, email or phone...',
    'clients.all': 'All',
    'clients.clientsLabel': 'Clients',
    'clients.professionals': 'Professionals',
    'clients.totalContacts': 'Total Contacts',
    'clients.noContactFound': 'No contact found',
    'clients.noContactYet': 'No contacts yet',
    'clients.adjustSearch': 'Try adjusting your search or filter',
    'clients.addMembersToStart': 'Add members to your projects to get started',
    'clients.firstContact': 'First contact',
    'clients.notProvided': 'Not provided',
    'clients.upgradeTitle': 'Client Portfolio',
    'clients.upgradeDesc': 'Manage all your clients and contacts in one place. This feature is exclusive to PRO subscribers.',

    // Revenue
    'revenue.title': 'Finances',
    'revenue.subtitle': 'Manage your income and expenses',
    'revenue.income': 'Income',
    'revenue.expense': 'Expense',
    'revenue.overview': 'Overview',
    'revenue.transactions': 'Transactions',
    'revenue.payments': 'Payments',
    'revenue.paymentRevenue': 'Payment Revenue',
    'revenue.approvedPayments': 'approved payment(s)',
    'revenue.noApprovedPayments': 'No approved payments yet',
    'revenue.approved': 'Approved',
    'revenue.project': 'Project',
    'revenue.paidAt': 'Paid on',
    'revenue.at': 'at',
    'revenue.edit': 'Edit',
    'revenue.totalBilling': 'Total Billing',
    'revenue.completeFinanceManager': 'Complete Finance Manager',
    'revenue.financeManagerDesc': 'Expense control, manual income, detailed charts, monthly and yearly summaries.',
    'revenue.upgradeToPro': 'Upgrade to PRO',
    'revenue.upgradeTitle': 'Billing and Finances',
    'revenue.upgradeDesc': 'Manage your income, expenses and view complete financial reports. This feature is exclusive to PRO subscribers.',

    // Project Details
    'project.back': 'Back',
    'project.refresh': 'Refresh',
    'project.payments': 'Payments',
    'project.archived': 'Archived',
    'project.members': 'Members',
    'project.discussion': 'Discussion',
    'project.logs': 'Logs',
    'project.drawings': 'Drawings',
    'project.saveTemplate': 'Save Template',
    'project.useTemplate': 'Use Template',
    'project.deleteProject': 'Delete Project',
    'project.addTab': 'New Tab',
    'project.addTopic': 'New Topic',
    'project.noTabs': 'No tabs created',
    'project.createFirstTab': 'Create your first tab to organize topics',
    'project.owner': 'Owner',
    'project.professional': 'Professional',
    'project.client': 'Client',
    'project.addMember': 'Add Member',
    'project.loadError': 'Error loading project',
    'project.refreshSuccess': 'Updated successfully',
    'project.charges': 'Charges',
    'project.noTabsYet': 'No tabs yet',
    'project.createFirstTabBtn': 'Create First Tab',
    'project.topicMoved': 'Topic moved!',
    'project.movedTo': 'Moved to',
    'project.errorMovingTopic': 'Error moving topic',
    'project.errorReordering': 'Error reordering',
    'project.couldNotSaveOrder': 'Could not save the new order.',
    'project.updateError': 'Error updating',
    'project.couldNotUpdate': 'Could not update the information',
    'project.drafts': 'Drafts',
    'projectDetails.tabLimitReached': 'Topic limit reached',
    'projectDetails.tabLimitReachedDesc': 'This tab has reached the maximum number of topics.',
    'board.tabLimitReached': 'Topic limit reached',
    'board.tabLimitReachedDesc': 'This tab has reached the maximum number of topics.',

    // Customization
    'customization.title': 'Customization',
    'customization.upgradeTitle': 'Customization',
    'customization.upgradeDesc': 'Customize your platform with your colors, logo and wallpaper. This feature is exclusive to PRO subscribers.',
    'customization.images': 'Images',
    'customization.imagesDesc': 'Customize your brand images',
    'customization.wallpaper': 'Background Wallpaper',
    'customization.logo': 'Company Logo',
    'customization.profilePhoto': 'Profile Photo',
    'customization.upload': 'Upload',
    'customization.uploadComplete': 'Upload complete',
    'customization.wallpaperUpdated': 'Wallpaper updated successfully.',
    'customization.logoUpdated': 'Logo updated successfully.',
    'customization.profileUpdated': 'Profile photo updated successfully.',
    'customization.fileTooLarge': 'File too large',
    'customization.maxSize': 'The file must be at most 5MB.',
    'customization.invalidType': 'Invalid file type',
    'customization.onlyImages': 'Only images are allowed.',
    'customization.recommendedWallpaper': 'Recommended: 1920x1080px, max 5MB',
    'customization.recommendedLogo': 'Recommended: square format (500x500px), transparent background, max 5MB',
    'customization.recommendedProfile': 'Recommended: square format (400x400px), max 5MB',
    'customization.colors': 'Color Palette',
    'customization.colorsDesc': 'Customize your interface colors',
    'customization.primaryColor': 'Primary Color',
    'customization.secondaryColor': 'Secondary Color',
    'customization.accentColor': 'Accent Color',
    // MyTasks
    'myTasks.title': 'My Tasks',
    'myTasks.subtitle': 'Organize your tasks by day',
    'myTasks.total': 'Total',
    'myTasks.pending': 'Pending',
    'myTasks.completed': 'Completed',
    'myTasks.overdue': 'Overdue',
    'myTasks.scheduled': 'Scheduled',
    'myTasks.list': 'List',
    'myTasks.calendar': 'Calendar',
    'myTasks.sortBy': 'Sort by',
    'myTasks.groupBy': 'Group by',
    'myTasks.scheduledDate': 'Scheduled Date',
    'myTasks.deadline': 'Deadline',
    'myTasks.startDate': 'Start Date',
    'myTasks.priority': 'Priority',
    'myTasks.project': 'Project',
    'myTasks.byDate': 'By Date',
    'myTasks.byProject': 'By Project',
    'myTasks.byStatus': 'By Status',
    'myTasks.noGrouping': 'No Grouping',
    'myTasks.noTasksFound': 'No tasks found',
    'myTasks.notAssigned': 'You are not assigned to any topic.',
    'myTasks.noPending': 'No pending tasks.',
    'myTasks.noCompleted': 'No completed tasks.',
    'myTasks.topicDeadline': 'Deadline',
    'myTasks.scheduledFor': 'Scheduled for',
    'myTasks.unscheduled': 'Task unscheduled',
    'myTasks.scheduleError': 'Error scheduling',
    'myTasks.couldNotUpdate': 'Could not update the date.',
    'myTasks.today': 'Today',
    'myTasks.dragHere': 'Drag tasks here',
    'myTasks.notScheduled': 'Not Scheduled',
    'myTasks.unscheduleHere': 'Drag tasks here to unschedule',
    'myTasks.more': 'more',
    'myTasks.topic': 'Topic',
    'myTasks.tab': 'Tab',
    'myTasks.overdueGroup': '⚠️ Overdue',
    'myTasks.todayGroup': '📅 Today',
    'myTasks.tomorrowGroup': '🌅 Tomorrow',
    'myTasks.thisWeekGroup': '📆 This Week',
    'myTasks.thisMonthGroup': '📋 This Month',
    'myTasks.futureGroup': '🔮 Future',
    'myTasks.noDateGroup': '📝 No Date Set',
    'myTasks.completedGroup': '✅ Completed',
    'myTasks.pendingGroup': '⏳ Pending',
    'myTasks.week': 'Week',
    'myTasks.month': 'Month',
    'myTasks.goToToday': 'Go to Today',
    'myTasks.sun': 'Sun',
    'myTasks.mon': 'Mon',
    'myTasks.tue': 'Tue',
    'myTasks.wed': 'Wed',
    'myTasks.thu': 'Thu',
    'myTasks.fri': 'Fri',
    'myTasks.sat': 'Sat',
    'myTasks.noProject': 'No Project',

    // Schedule
    'schedule.title': 'Schedule',
    'schedule.newEvent': 'New Event',
    'schedule.editEvent': 'Edit Event',
    'schedule.eventTitle': 'Title',
    'schedule.description': 'Description',
    'schedule.date': 'Date',
    'schedule.allDay': 'All day',
    'schedule.start': 'Start',
    'schedule.end': 'End',
    'schedule.color': 'Color',
    'schedule.purple': 'Purple',
    'schedule.blue': 'Blue',
    'schedule.green': 'Green',
    'schedule.yellow': 'Yellow',
    'schedule.red': 'Red',
    'schedule.pink': 'Pink',
    'schedule.save': 'Save',
    'schedule.create': 'Create',
    'schedule.cancel': 'Cancel',
    'schedule.loadError': 'Error loading events',
    'schedule.eventUpdated': 'Event updated!',
    'schedule.eventCreated': 'Event created!',
    'schedule.saveError': 'Error saving event',
    'schedule.eventRemoved': 'Event removed!',
    'schedule.removeError': 'Error removing event',
    'schedule.noEvents': 'No events',
    'schedule.upgradeDesc': 'Manage your schedule and appointments. This feature is available for Prime and PRO subscribers.',

    // Achievements
    'achievements.title': 'Achievements',
    'achievements.trackProgress': 'Track your progress',
    'achievements.yourStreak': 'Your Streak',
    'achievements.yourProgress': 'Your Progress',
    'achievements.projects': 'Projects',
    'achievements.tasks': 'Tasks',
    'achievements.clients': 'Clients',
    'achievements.payments': 'Payments',
    'achievements.comments': 'Comments',
    'achievements.allAchievements': 'All Achievements',

    // Common
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.back': 'Back',
    'common.close': 'Close',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.export': 'Export',
    'common.required': 'Required',

    // Push Notifications
    'pushNotifications': 'Push Notifications',
    'pushEnabled': 'Notifications enabled!',
    'pushEnabledDesc': 'You will receive alerts even when the app is closed.',
    'pushDisabled': 'Notifications disabled',
    'pushDisabledDesc': 'You will no longer receive push alerts.',
    'pushEnable': 'Enable Notifications',
    'pushDisable': 'Disable',
    'pushError': 'Notification error',
    'pushErrorDesc': 'Could not configure notifications.',
    'pushNotSupported': 'Not supported',
    'pushNotSupportedDesc': 'Your browser does not support push notifications.',
    'pushPermissionDenied': 'Permission denied',
    'pushPermissionDeniedDesc': 'You can change this in your browser settings.',

    // Dialogs
    'dialog.newProject': 'New Project',
    'dialog.createProject': 'Create New Project',
    'dialog.createProjectDesc': 'Create a new project to organize your tasks and collaborate with your team.',
    'dialog.projectName': 'Project Name',
    'dialog.projectNamePlaceholder': 'Ex: Corporate Website',
    'dialog.descriptionOptional': 'Description (optional)',
    'dialog.descriptionPlaceholder': 'Describe the goals and scope of the project...',
    'dialog.nameRequired': 'Name required',
    'dialog.enterProjectName': 'Please enter a name for the project.',
    'dialog.authError': 'Authentication error',
    'dialog.needLogin': 'You need to be logged in to create projects.',
    'dialog.projectCreated': 'Project created!',
    'dialog.projectCreatedDesc': 'Your project was created successfully.',
    'dialog.createError': 'Error creating project',
    'dialog.createProjectBtn': 'Create Project',
    
    'dialog.editProject': 'Edit Project',
    'dialog.editProjectDesc': 'Change the title and description of the project.',
    'dialog.projectUpdated': 'Project updated!',
    'dialog.updateError': 'Error updating project',
    
    'dialog.deleteProject': 'Delete Project',
    'dialog.deleteWarning': 'This action is irreversible and will permanently delete:',
    'dialog.deleteAllTabs': 'All project tabs',
    'dialog.deleteAllTopics': 'All topics and their descriptions',
    'dialog.deleteAllMembers': 'All associated members',
    'dialog.deleteAllCharges': 'All PIX charges created',
    'dialog.typeToConfirm': 'To confirm, type the project name:',
    'dialog.confirmProjectName': 'Confirm project name',
    'dialog.typeProjectName': 'Type the project name',
    'dialog.incorrectName': 'Incorrect name',
    'dialog.typeExactName': 'Type the exact project name to confirm deletion.',
    'dialog.projectDeleted': 'Project deleted',
    'dialog.projectDeletedDesc': 'The project and all its data have been successfully removed.',
    'dialog.deleteError': 'Error deleting project',
    'dialog.deleting': 'Deleting...',
    
    'dialog.leaveProject': 'Leave Project',
    'dialog.leaveConfirm': 'Are you sure you want to leave the project',
    'dialog.leaveWarning': 'You will lose access to all information and will no longer be able to collaborate on this project unless you are invited again.',
    'dialog.leftProject': 'You left the project',
    'dialog.leftProjectDesc': 'You are no longer part of',
    'dialog.leaveError': 'Error leaving project',
    'dialog.leaving': 'Leaving...',
    
    'dialog.addMember': 'Add Member',
    'dialog.addMemberDesc': 'Add a member to the project by registered email on the platform.',
    'dialog.email': 'Email',
    'dialog.emailPlaceholder': 'email@example.com',
    'dialog.role': 'Role',
    'dialog.client': 'Client',
    'dialog.professional': 'Professional',
    'dialog.validationError': 'Validation error',
    'dialog.userNotFound': 'User not found with this email',
    'dialog.alreadyMember': 'This user is already a member of the project',
    'dialog.memberAdded': 'Member added!',
    'dialog.addedAs': 'was added to the project as',
    'dialog.addMemberError': 'Error adding member',
    
    'dialog.newTab': 'New Tab',
    'dialog.newTabDesc': 'Create a new tab to organize your topics.',
    'dialog.tabName': 'Tab Name',
    'dialog.tabNamePlaceholder': 'Ex: Design, Development, Marketing',
    'dialog.tabNameRequired': 'Please enter a name for the tab.',
    'dialog.tabCreated': 'Tab created!',
    'dialog.tabCreatedDesc': 'The tab was created successfully.',
    'dialog.tabError': 'Error creating tab',
    'dialog.createTab': 'Create Tab',
    
    'dialog.newTopic': 'New Topic',
    'dialog.newTopicDesc': 'Add a new topic to organize your tasks.',
    'dialog.topicName': 'Topic Name',
    'dialog.topicNamePlaceholder': 'Ex: Create landing page',
    'dialog.topicNameRequired': 'Please enter a name for the topic.',
    'dialog.description': 'Description',
    'dialog.descriptionBrief': 'Brief description of the topic (optional)',
    'dialog.content': 'Content',
    'dialog.contentPlaceholder': 'Add notes, detailed information or any relevant content...',
    'dialog.color': 'Color',
    'dialog.topicCreated': 'Topic created!',
    'dialog.topicCreatedDesc': 'The topic was created successfully.',
    'dialog.topicError': 'Error creating topic',
    'dialog.createTopic': 'Create Topic',
    
    'dialog.editTopic': 'Edit Topic',
    'dialog.editTopicDesc': 'Edit the title, description, deadline or move to another tab.',
    'dialog.title': 'Title',
    'dialog.startDate': 'Start Date',
    'dialog.endDate': 'End Date',
    'dialog.start': 'Start',
    'dialog.end': 'End',
    'dialog.clear': 'Clear',
    'dialog.responsible': 'Responsible',
    'dialog.selectResponsible': 'Select a responsible',
    'dialog.noResponsible': 'No responsible',
    'dialog.moveToTab': 'Move to tab',
    'dialog.current': 'current',
    'dialog.topicMoved': 'Topic moved!',
    'dialog.topicMovedTo': 'The topic was moved to',
    'dialog.topicUpdated': 'Topic updated!',
    'dialog.topicUpdatedDesc': 'Changes were saved successfully.',
    'dialog.topicUpdateError': 'Error updating topic',
    'dialog.moveAndSave': 'Move and Save',
    'dialog.saveChanges': 'Save Changes',
    
    'dialog.archiveTopic': 'Archive topic',
    'dialog.archiveConfirm': 'Are you sure you want to archive',
    'dialog.archiveWarning': 'The topic will be automatically deleted after 30 days, but you can restore it before that.',
    'dialog.topicArchived': 'Topic archived',
    'dialog.topicArchivedDesc': 'The topic was archived and will be deleted after 30 days.',
    'dialog.archiveError': 'Error archiving topic',
    'dialog.archive': 'Archive',
    'dialog.sendNotification': 'Send Notification',
    'dialog.sendNotificationDesc': 'Send a notification to a project member. It will appear on their dashboard for 24 hours.',
    'dialog.selectMember': 'Select member',
    'dialog.allMembers': 'all members',
    'dialog.selectMemberPlaceholder': 'Choose a member',
    'dialog.message': 'Message',
    'dialog.messagePlaceholder': 'Type your message...',
    'dialog.notificationSent': 'Notification sent',
    'dialog.notificationSentDesc': 'The notification was sent successfully.',
    'dialog.notificationSentAllDesc': 'Notification sent to all members.',
    
    'dialog.upgradePlan': 'Upgrade your plan',
    'dialog.limitProjects': 'You have reached the project limit of your plan.',
    'dialog.limitMembers': 'You have reached the member limit for this project.',
    'dialog.limitTabs': 'You have reached the tab limit for this project.',
    'dialog.limitTopics': 'You have reached the topic limit for this tab.',
    'dialog.limitDefault': 'You have reached a limit of your plan.',
    'dialog.upTo': 'Up to',
    'dialog.projects': 'projects',
    'dialog.membersPerProject': 'members per project',
    'dialog.basicExport': 'Basic export',
    'dialog.advancedCustomization': 'Advanced customization',
    'dialog.reportsAnalytics': 'Reports and analytics',
    'dialog.prioritySupport': 'Priority support',
    'dialog.advancedExport': 'Advanced export',
    'dialog.subscribePrime': 'Subscribe Prime',
    'dialog.subscribePro': 'Subscribe PRO',
    'dialog.upgradeToPro': 'Upgrade to PRO',
    'dialog.recommended': 'Recommended',
    'dialog.processing': 'Processing...',
    'dialog.youHave': 'you have',
    
    // ProjectDetails
    'projectDetails.errorLoading': 'Error loading project',
    'projectDetails.updated': 'Updated successfully',
    'projectDetails.updatedDesc': 'All information has been updated',
    'projectDetails.updateError': 'Error updating',
    'projectDetails.updateErrorDesc': 'Could not update the information',
    'projectDetails.topicMoved': 'Topic moved!',
    'projectDetails.movedTo': 'Moved to',
    'projectDetails.errorMoving': 'Error moving topic',
    'projectDetails.errorReorder': 'Error reordering',
    'projectDetails.errorReorderDesc': 'Could not save the new order.',
    'projectDetails.refresh': 'Refresh',
    'projectDetails.saveAsTemplate': 'Save as Template',
    'projectDetails.template': 'Template',
    'projectDetails.useTemplate': 'Use Template',
    'projectDetails.deleteProject': 'Delete Project',
    'projectDetails.delete': 'Delete',
    'projectDetails.charges': 'Charges',
    'projectDetails.members': 'Members',
    'projectDetails.archived': 'Archived',
    'projectDetails.drawings': 'Drawings',
    'projectDetails.discussion': 'Discussion',
    'projectDetails.logs': 'Logs',
    'projectDetails.newTab': 'New Tab',
    'projectDetails.tabs': 'Tabs',
    'projectDetails.limit': 'Limit',
    'projectDetails.noTabsYet': 'No tabs yet',
    'projectDetails.noTabsDesc': 'Create your first tab to start organizing project topics.',
    'projectDetails.createFirstTab': 'Create First Tab',
    'projectDetails.projectMembers': 'Project Members',
    'projectDetails.addMember': 'Add Member',
    'projectDetails.projectDiscussion': 'Project Discussion',
    
    // Board/Topic Components
    'board.removePriority': 'Remove priority',
    'board.markAsPriority': 'Mark as priority',
    'board.settings': 'Settings',
    'board.archive': 'Archive',
    'board.edit': 'Edit',
    'board.deadline': 'Deadline',
    'board.delete': 'Delete',
    'board.dropHere': 'Drop here',
    'board.noTopics': 'No topics',
    'board.add': 'Add',
    'board.topicMoved': 'Topic moved!',
    'board.movedTo': 'Moved to',
    'board.errorMoving': 'Error moving',
    'board.errorUpdatingTopic': 'Error updating topic',
    'board.errorUpdatingPriority': 'Error updating priority',
    
    // Members List
    'members.roleUpdated': 'Role updated',
    'members.roleUpdatedDesc': 'The member\'s role has been changed successfully.',
    'members.errorUpdatingRole': 'Error updating role',
    'members.memberRemoved': 'Member removed',
    'members.memberRemovedDesc': 'The member has been removed from the project.',
    'members.errorRemoving': 'Error removing member',
    'members.professional': 'Professional',
    'members.client': 'Client',
    'members.addedOn': 'Added on',
    'members.noMembers': 'No members added yet.',
    'members.removeMember': 'Remove member',
    'members.removeConfirm': 'Are you sure you want to remove',
    'members.removeWarning': 'from the project? This action cannot be undone.',
    'members.remove': 'Remove',

    // Subscription Page
    'subscription.choosePlan': 'Choose your plan',
    'subscription.unlockPotential': 'Unlock Taskeer\'s full potential',
    'subscription.currentPlan': 'Current plan',
    'subscription.recommended': 'Recommended',
    'subscription.daysFree': 'days free',
    'subscription.yourPlan': 'Your Plan',
    'subscription.month': 'month',
    'subscription.freeTrial': '7-day free trial',
    'subscription.currentPlanBtn': 'Current Plan',
    'subscription.manage': 'Manage',
    'subscription.downgrade': 'Downgrade',
    'subscription.basicPlan': 'Basic Plan',
    'subscription.processing': 'Processing...',
    'subscription.startTrial': 'Start {days}-day Trial',
    'subscription.subscribeNow': 'Subscribe Now',
    'subscription.upgrade': 'Upgrade',
    'subscription.startingFrom': 'Starting from',
    'subscription.upgradeToPrime': 'Upgrade Now',
    'subscription.back': 'Back',
    'subscription.primeOrPro': 'Taskeer Prime or PRO',
    'subscription.exclusiveFeatures': 'Access exclusive features',
    'subscription.trialInfo': 'The PRO plan includes a 7-day free trial. After the trial period, you will be charged automatically or you can cancel at any time.',
    'subscription.backToDashboard': 'Back to Dashboard',
    'subscription.free': 'Free',
    'subscription.feature1Project': '1 project',
    'subscription.feature1Member': '1 member per project',
    'subscription.feature3Tabs': '3 tabs per project',
    'subscription.feature5Topics': '5 topics per tab',
    'subscription.featureBasic': 'Basic features',
    'subscription.feature10Projects': 'Up to 10 projects',
    'subscription.feature3Members': 'Up to 3 members per project',
    'subscription.feature20Tabs': '20 tabs per project',
    'subscription.feature30Topics': '30 topics per tab',
    'subscription.featureExportCSV': 'Basic export (CSV)',
    'subscription.featureEmailSupport': 'Email support',
    'subscription.feature80Projects': 'Up to 80 projects',
    'subscription.feature10Members': 'Up to 10 members per project',
    'subscription.featureCustomization': 'Advanced customization',
    'subscription.featureAnalytics': 'Reports and analytics',
    'subscription.featureClients': 'Client portfolio',
    'subscription.featureExportAdvanced': 'Advanced export (Excel, PDF)',
  },
  es: {
    // Header
    'header.enter': 'Entrar',
    'header.news': 'Novedades',
    
    // Hero
    'hero.badge': '¡Mucho más que Notion/Trello y 100% brasileño!',
    'hero.title1': 'Gestiona tus proyectos y',
    'hero.title2': 'recibe pagos',
    'hero.title3': 'en un solo lugar',
    'hero.subtitle': 'Organiza tareas, colabora con clientes y recibe por PIX. Todo personalizado con tu marca.',
    'hero.cta': '¡Empezar Ahora!',
    'hero.demo': 'Ver Demostración',
    'hero.freeNote': 'Empieza gratis — sin tarjeta de crédito',
    
    // Live Counter
    'counter.online': 'en línea ahora',
    'counter.professionals': 'profesionales',
    'counter.projects': 'proyectos creados',
    'counter.charges': 'en cobros',
    
    // Offer Banner
    'offer.special': '🎉 Oferta Especial:',
    'offer.discount': '50% OFF',
    'offer.text': '¡en el primer mes de PRO!',
    'offer.cta': 'Aprovechar',
    
    // Pain Points
    'pain.title': '¿Todavía trabajas',
    'pain.title2': 'en modo caos?',
    'pain.1.pain': 'Cliente pregunta el estado por WhatsApp',
    'pain.1.solution': 'Cliente acompaña todo en tiempo real',
    'pain.1.detail': 'Sin más "¿Cómo va?"',
    'pain.2.pain': 'Pierdes horas organizando tareas en hojas de cálculo',
    'pain.2.solution': 'Organización automática en pestañas y temas',
    'pain.2.detail': 'Tu tiempo vale más',
    'pain.3.pain': 'No encuentras archivos e información',
    'pain.3.solution': 'Todo centralizado por proyecto',
    'pain.3.detail': 'Se acabó el desorden',
    'pain.4.pain': 'Cobras por WhatsApp y pierdes control',
    'pain.4.solution': 'Cobros PIX con confirmación automática',
    'pain.4.detail': 'Profesionalismo que convierte',
    'pain.5.pain': 'No sabes cuántos clientes tienes',
    'pain.5.solution': 'Cartera de clientes organizada',
    'pain.5.detail': 'Conoce tu negocio',
    
    // What Is Section
    'whatIs.badge': 'Conoce Taskeer',
    'whatIs.title': '¿Qué es Taskeer?',
    'whatIs.description': 'Taskeer es la plataforma completa para freelancers y proveedores de servicios digitales. Gestiona proyectos, colabora con clientes y recibe pagos por PIX — todo en un solo lugar.',
    'whatIs.description2': 'Reemplaza Notion, Trello y hojas de cálculo de facturación por una solución 100% brasileña, hecha especialmente para quienes necesitan organización profesional y control financiero integrado.',
    'whatIs.highlight1': 'Proyectos organizados',
    'whatIs.highlight2': 'Colaboración en tiempo real',
    'whatIs.highlight3': 'Pagos PIX integrados',
    'whatIs.highlight4': 'Métricas e informes',

    // How It Works
    'how.title': 'Cómo Funciona',
    'how.subtitle': 'Mira qué simple es usar Taskeer',
    'how.step': 'Paso',
    'how.playing': 'Reproduciendo',
    'how.paused': 'Pausado',
    'how.step1.title': 'Crea tu Proyecto',
    'how.step1.desc': 'Comienza creando un nuevo proyecto para tu cliente o servicio',
    'how.step2.title': 'Organiza en Pestañas y Temas',
    'how.step2.desc': 'Estructura el trabajo en pestañas con temas detallados',
    'how.step3.title': 'Invita a tu Equipo',
    'how.step3.desc': 'Añade clientes y profesionales para colaborar',
    'how.step4.title': 'Genera Cobros PIX',
    'how.step4.desc': 'Crea códigos QR y recibe pagos en la plataforma',
    'how.step5.title': 'Acompaña Todo',
    'how.step5.desc': 'Dashboard con métricas de proyectos y facturación',
    
    // Stats
    'stats.badge': 'Resultados Comprobados',
    'stats.title': 'Números que Hablan por Sí Mismos',
    'stats.subtitle': 'Profesionales que usan Taskeer transforman completamente su rutina de trabajo',
    'stats.disclaimer': '* Basado en feedback de profesionales que migraron a Taskeer',
    'stats.1.label': 'Ahorradas por semana',
    'stats.1.desc': 'Menos tiempo perdido con organización manual',
    'stats.2.label': 'Más organización',
    'stats.2.desc': 'Proyectos estructurados y fáciles de seguir',
    'stats.3.label': 'Más satisfacción del cliente',
    'stats.3.desc': 'Transparencia total en el seguimiento',
    'stats.4.label': 'Control financiero',
    'stats.4.desc': 'Cobros PIX integrados y rastreables',
    
    // Demo
    'demo.badge': 'Míralo en la Práctica',
    'demo.title': 'Simple, Intuitivo, Poderoso',
    'demo.subtitle': 'Mira qué fácil es gestionar tus proyectos y recibir pagos',
    'demo.cta': 'Pruébalo Gratis',
    'demo.topics': 'Temas',
    'demo.members': 'Miembros',
    'demo.payments': 'Pagos',
    'demo.addTopic': 'Añadir tema',
    'demo.paymentConfirmed': '¡Pago Confirmado!',
    'demo.receivedSuccess': 'recibido con éxito',
    'demo.pixCharge': 'Cobro PIX',
    'demo.scanQr': 'Escanea el código QR para pagar',
    'demo.topicCompleted': 'Tema completado',
    'demo.justNow': 'Ahora mismo',
    'demo.clientConnected': 'Cliente conectado',
    
    // Testimonials
    'testimonials.badge': 'Testimonios',
    'testimonials.title': 'Profesionales que Transformaron su Negocio',
    'testimonials.subtitle': 'Mira lo que dicen los prestadores de servicios que ya usan Taskeer',
    'testimonials.trust': 'Únete a cientos de profesionales que ya confían en Taskeer',
    'testimonials.rating': '4.9/5 de satisfacción',
    'testimonials.projects': '+500 proyectos creados',
    'testimonials.charges': '+R$ 100k en cobros generados',
    
    // Features
    'features.title': 'Todo lo que necesitas',
    'features.subtitle': 'Herramientas profesionales para gestionar tus servicios',
    'features.projectManagement': 'Gestión de Proyectos',
    'features.projectManagementDesc': 'Organiza proyectos en pestañas, temas y descripciones detalladas',
    'features.kanban': 'Vista Kanban',
    'features.kanbanDesc': 'Organiza tus temas en columnas estilo Trello',
    'features.teamCollab': 'Colaboración en Equipo',
    'features.teamCollabDesc': 'Invita clientes y profesionales a trabajar juntos',
    'features.pixCharges': 'Cobros con PIX',
    'features.pixChargesDesc': 'Genera códigos QR y gestiona pagos directamente en la plataforma',
    'features.budgets': 'Generación de Presupuestos',
    'features.budgetsDesc': 'Crea presupuestos profesionales personalizados para tus clientes',
    'features.finance': 'Gestor Financiero',
    'features.financeDesc': 'Controla ingresos, gastos y visualiza informes completos',
    'features.myTasks': 'Mis Tareas',
    'features.myTasksDesc': 'Visualiza todas tus tareas en un solo lugar con calendario',
    'features.clientPortal': 'Portal del Cliente',
    'features.clientPortalDesc': 'Los clientes siguen proyectos y confirman pagos fácilmente',
    'features.achievements': 'Logros',
    'features.achievementsDesc': 'Gamificación con insignias y logros para motivar tu productividad',
    'features.dashboard': 'Dashboard Profesional',
    'features.dashboardDesc': 'Métricas de facturación, clientes y proyectos en un solo lugar',
    'features.notifications': 'Notificaciones Push',
    'features.notificationsDesc': 'Recibe alertas de plazos, pagos y actividades en el navegador',
    'features.customization': 'Personalización Completa',
    'features.customizationDesc': 'Personaliza tu plataforma con tus colores y logo',
    
    // Pricing
    'pricing.title': 'Elige tu plan',
    'pricing.subtitle': 'Planes flexibles para tu crecimiento',
    'pricing.recommended': 'Recomendado',
    'pricing.month': '/mes',
    'pricing.prime.projects': 'Hasta 10 proyectos',
    'pricing.prime.members': 'Hasta 3 miembros por proyecto',
    'pricing.prime.charges': 'Cobros PIX ilimitados',
    'pricing.prime.schedule': 'Agenda',
    'pricing.prime.budgets': 'Generación de presupuestos',
    'pricing.prime.cta': 'Empezar con Prime',
    'pricing.pro.projects': 'Hasta 80 proyectos',
    'pricing.pro.members': 'Hasta 10 miembros por proyecto',
    'pricing.pro.customization': 'Personalización avanzada',
    'pricing.pro.clients': 'Cartera de clientes',
    'pricing.pro.dashboard': 'Dashboard profesional',
    'pricing.pro.billing': 'Facturación',
    'pricing.pro.export': 'Exportaciones',
    'pricing.pro.andMore': 'Y mucho más...',
    'pricing.pro.cta': 'Empezar con PRO',
    'pricing.exclusive': 'Exclusivo',
    'pricing.noClientSub': 'Tus clientes no necesitan suscripción',
    'pricing.free.name': 'Gratis',
    'pricing.free.price': '$0',
    'pricing.free.badge': 'Para siempre',
    'pricing.free.projects': '1 proyecto activo',
    'pricing.free.client': '1 cliente invitado',
    'pricing.free.structure': '3 pestañas y 5 temas por proyecto',
    'pricing.free.tools': 'Todas las herramientas gratuitas',
    'pricing.free.noCard': 'Sin tarjeta de crédito',
    'pricing.free.cta': 'Crear cuenta gratis',
    
    // FAQ
    'faq.badge': 'FAQ',
    'faq.title': 'Preguntas Frecuentes',
    'faq.subtitle': 'Todo lo que necesitas saber sobre Taskeer',
    'faq.stillQuestions': '¿Aún tienes dudas?',
    'faq.contact': 'Contáctanos',
    'faq.1.q': '¿Cómo funciona el período de prueba gratuito?',
    'faq.1.a': 'Taskeer PRO ofrece 7 días de prueba gratis después de añadir una tarjeta de crédito. Durante este período, tienes acceso a todas las funcionalidades PRO. Si no quieres continuar, solo cancela antes de que termine y no se te cobrará.',
    'faq.2.q': '¿Mis clientes necesitan pagar para usar la plataforma?',
    'faq.2.a': '¡No! Tus clientes no necesitan suscripción. Pueden acceder a los proyectos, seguir el progreso y realizar pagos sin costo alguno. Solo el profesional necesita una cuenta paga.',
    'faq.3.q': '¿Cómo funcionan los cobros por PIX?',
    'faq.3.a': 'Registras tu clave PIX una vez en el sistema. Al crear un cobro, generamos automáticamente un código QR válido para cualquier banco brasileño. El cliente escanea, paga y tú acompañas todo en tiempo real en Taskeer.',
    'faq.4.q': '¿Puedo cancelar mi suscripción en cualquier momento?',
    'faq.4.a': '¡Sí! Puedes cancelar tu suscripción cuando quieras. Tus proyectos siguen accesibles hasta el final del período pagado. Después de 30 días sin renovación, los proyectos se archivan para eventual recuperación.',
    'faq.5.q': '¿Cuántos proyectos y clientes puedo tener?',
    'faq.5.a': 'En el plan Prime, puedes tener hasta 10 proyectos con 3 miembros cada uno. En el plan PRO, hasta 80 proyectos con 10 miembros cada uno. Para la mayoría de freelancers, PRO ofrece espacio de sobra para crecer.',
    'faq.6.q': '¿Los datos de mis proyectos son seguros?',
    'faq.6.a': '¡Absolutamente! Utilizamos infraestructura de última generación con encriptación de punta. Tus datos y los de tus clientes están protegidos con los más altos estándares de seguridad del mercado.',
    'faq.7.q': '¿Puedo usar Taskeer en el celular?',
    'faq.7.a': '¡Sí! Taskeer es 100% responsivo y funciona perfectamente en cualquier dispositivo: celular, tablet o computadora. Puedes gestionar tus proyectos desde donde estés.',
    'faq.8.q': '¿Cómo funciona la visualización Kanban?',
    'faq.8.a': 'La visualización Kanban transforma tus pestañas en columnas, permitiendo arrastrar y soltar temas entre ellas. Perfecto para quienes les gusta visualizar el flujo de trabajo estilo Trello, pero con toda la potencia de Taskeer.',
    
    // Final CTA
    'cta.badge': 'Comienza gratis, sin tarjeta de crédito',
    'cta.title1': 'Basta de caos.',
    'cta.title2': 'Organiza tu negocio hoy.',
    'cta.subtitle': 'Únete a cientos de profesionales que ya transformaron la forma como gestionan proyectos y reciben pagos. Tu nueva central comienza ahora.',
    'cta.createAccount': 'Crear Mi Cuenta Gratis',
    'cta.viewPlans': 'Ver Planes y Precios',
    'cta.setup': 'Configuración en 2 minutos',
    'cta.noCommitment': 'Sin compromiso',
    'cta.support': 'Soporte en portugués',
    
    // Updates
    'updates.title': 'Novedades de la Plataforma',
    'updates.subtitle': 'Sigue las últimas actualizaciones de Taskeer',
    'updates.viewAll': 'Ver Todas las Novedades',
    'updates.clientPortal': 'Portal del Cliente',
    'updates.clientPortalDesc': 'Área exclusiva para que los clientes sigan proyectos y pagos.',
    'updates.pushNotifications': 'Notificaciones Push en el Navegador',
    'updates.pushNotificationsDesc': 'Recibe alertas de plazos, pagos y actividades importantes.',
    'updates.projectTemplates': 'Plantillas de Proyectos',
    'updates.projectTemplatesDesc': 'Guarda estructuras de proyectos y reutilízalas en nuevos proyectos.',
    'updates.financeManager': 'Gestor Financiero Completo',
    'updates.financeManagerDesc': 'Sistema completo para control de ingresos, gastos e informes.',
    'updates.proDashboard': 'Dashboard Profesional',
    'updates.proDashboardDesc': 'Visualiza métricas importantes de tu negocio en un solo lugar.',
    
    // Footer
    'footer.about': 'Plataforma completa para prestadores de servicios digitales gestionar proyectos y recibir pagos.',
    'footer.quickLinks': 'Enlaces Rápidos',
    'footer.enter': 'Entrar',
    'footer.createAccount': 'Crear Cuenta',
    'footer.plans': 'Planes',
    'footer.contact': 'Contacto',
    'footer.rights': 'Todos los derechos reservados.',
    
    // Language selector
    'language.pt': 'Português',
    'language.en': 'English',
    'language.es': 'Español',

    // Auth Page
    'auth.subtitle': 'Gestiona tus proyectos con estilo',
    'auth.login': 'Entrar',
    'auth.signup': 'Crear Cuenta',
    'auth.email': 'Correo electrónico',
    'auth.password': 'Contraseña',
    'auth.confirmPassword': 'Confirmar Contraseña',
    'auth.fullName': 'Nombre Completo',
    'auth.country': 'País',
    'auth.phone': 'Teléfono (con código de área)',
    'auth.birthDate': 'Fecha de Nacimiento',
    'auth.loggingIn': 'Entrando...',
    'auth.creatingAccount': 'Creando...',
    'auth.backToHome': 'Volver al inicio',
    'auth.loginSuccess': '¡Inicio de sesión exitoso!',
    'auth.loginSuccessDesc': 'Bienvenido de vuelta a Taskeer',
    'auth.loginError': 'Error al iniciar sesión',
    'auth.signupSuccess': '¡Cuenta creada con éxito!',
    'auth.signupSuccessDesc': 'Ya puedes iniciar sesión',
    'auth.signupError': 'Error al crear cuenta',
    'auth.validationError': 'Error de validación',
    'auth.captchaRequired': 'Por favor, complete la verificación de seguridad',
    'auth.captchaFailed': 'La verificación de seguridad falló. Inténtalo de nuevo.',
    'auth.forgotPassword': 'Olvidé mi contraseña',
    'auth.forgotPasswordDesc': 'Ingresa tu correo para recibir un enlace de recuperación',
    'auth.sendResetLink': 'Enviar enlace de recuperación',
    'auth.sending': 'Enviando...',
    'auth.backToLogin': 'Volver al inicio de sesión',
    'auth.resetEmailSent': '¡Correo enviado!',
    'auth.resetEmailSentDesc': 'Revisa tu bandeja de entrada para restablecer tu contraseña',
    'auth.resetError': 'Error al enviar correo',
    'auth.enterEmail': 'Ingresa tu correo',
    'auth.newPassword': 'Nueva Contraseña',
    'auth.confirmNewPassword': 'Confirmar Nueva Contraseña',
    'auth.enterNewPassword': 'Ingresa tu nueva contraseña abajo',
    'auth.defineNewPassword': 'Define tu nueva contraseña',
    'auth.updatePassword': 'Actualizar Contraseña',
    'auth.updating': 'Actualizando...',
    'auth.passwordUpdated': '¡Contraseña actualizada!',
    'auth.passwordUpdatedDesc': 'Tu contraseña ha sido cambiada con éxito. Inicia sesión con tu nueva contraseña.',
    'auth.passwordTooShort': 'La contraseña debe tener al menos 6 caracteres',
    'auth.passwordsDoNotMatch': 'Las contraseñas no coinciden',
    'auth.invalidResetLink': 'Enlace inválido o expirado',
    'auth.invalidResetLinkDesc': 'Este enlace de recuperación es inválido o ya expiró. Solicita un nuevo enlace.',
    'auth.profession': 'Profesión',
    'auth.selectProfession': 'Selecciona tu profesión',
    'auth.professions.freelancer': 'Freelancer / Diseñador / Dev',
    'auth.professions.autonomo': 'Autónomo / Prestador de Servicios',
    'auth.professions.agencia': 'Agencia / Estudio',
    'auth.professions.empresa': 'Empresa / Startup',
    'auth.professions.estudante': 'Estudiante',
    'auth.professions.outro': 'Otro',
    'auth.platformInterest': '¿Qué buscas en la plataforma?',
    'auth.selectInterest': 'Selecciona tu objetivo',
    'auth.interests.gerenciarProjetos': 'Gestionar proyectos de clientes',
    'auth.interests.cobrarClientes': 'Cobrar clientes con PIX',
    'auth.interests.organizarTarefas': 'Organizar mis tareas',
    'auth.interests.colaborarEquipe': 'Colaborar con equipo',
    'auth.interests.controleFinanceiro': 'Control financiero',
    'auth.interests.tudo': '¡Todo lo anterior!',

    // PWA Install
    'installTitle': 'Instalar Taskeer',
    'installSubtitle': 'Ten Taskeer siempre a mano en tu celular',
    'appInstalled': '¡App ya instalada!',
    'goToDashboard': 'Ir al Dashboard',
    'installOnIOS': 'Instalar en iPhone/iPad',
    'installOnAndroid': 'Instalar en Android',
    'followSteps': 'Sigue los pasos a continuación:',
    'tapShare': 'Toca el botón Compartir',
    'bottomBar': 'En la barra inferior de Safari',
    'selectAddToHome': 'Selecciona "Añadir a pantalla de inicio"',
    'scrollDown': 'Desplaza la lista si es necesario',
    'tapAdd': 'Toca "Añadir"',
    'installApp': 'Instalar App',
    'new': 'Nuevo',
    'installDescription': 'Accede a Taskeer directamente desde la pantalla de inicio de tu celular',
    'openMenu': 'Abre el menú del navegador',
    'topRight': 'Tres puntos en la esquina superior derecha',
    'selectInstallApp': 'Selecciona "Instalar app" o "Añadir a pantalla de inicio"',
    'confirmInstall': 'Confirma la instalación',
    'backToHome': 'Volver al inicio',

    // Dashboard
    'dashboard.welcome': 'Bienvenido,',
    'dashboard.activeProjects': 'proyecto(s) activo(s)',
    'dashboard.myProjects': 'Mis Proyectos',
    'dashboard.myTasks': 'Mis Tareas',
    'dashboard.clientPortal': 'Portal del Cliente',
    'dashboard.news': 'Novedades',
    'dashboard.achievements': 'Logros',
    'dashboard.schedule': 'Agenda',
    'dashboard.proDashboard': 'Dashboard Profesional',
    'dashboard.billing': 'Facturación',
    'dashboard.myClients': 'Mis Clientes',
    'dashboard.customization': 'Personalización',
    'dashboard.archived': 'Archivados',
    'dashboard.myProfile': 'Mi Perfil',
    'dashboard.logout': 'Salir',
    'dashboard.logoutSuccess': 'Sesión cerrada',
    'dashboard.logoutSuccessDesc': '¡Hasta pronto!',
    'dashboard.loadError': 'Error al cargar proyectos',
    'dashboard.notifications': 'Notificaciones',
    'dashboard.generateBudget': 'Generar Presupuesto',
    'dashboard.searchProjects': 'Buscar proyectos...',
    'dashboard.all': 'Todos',
    'dashboard.favorites': 'Favoritos',
    'dashboard.recent': 'Recientes',
    'dashboard.noProjects': 'No se encontraron proyectos',
    'dashboard.noProjectsDesc': 'Crea tu primer proyecto para comenzar',
    'dashboard.createFirst': 'Crear Primer Proyecto',
    'dashboard.noFavorites': 'Sin favoritos',
    'dashboard.noFavoritesDesc': 'Haz clic en la estrella de los proyectos para agregarlos a favoritos',
    'dashboard.noRecent': 'Sin proyectos recientes',
    'dashboard.noRecentDesc': 'Tus proyectos accedidos recientemente aparecerán aquí',
    'dashboard.noResults': 'Sin resultados',
    'dashboard.noResultsDesc': 'Intenta buscar con otros términos',
    'dashboard.clearSearch': 'Limpiar búsqueda',
    'dashboard.archivePolicy': 'Los proyectos no renovados se archivan después de 30 días y se eliminan después de otros 30 días.',
    'dashboard.unlockPotential': 'Desbloquea tu máximo potencial',
    'dashboard.customizeWithBrand': 'Personaliza con tu marca',
    'dashboard.financeManagement': 'Gestión Financiera',
    'dashboard.fullFinanceControl': 'Control completo de finanzas',
    'dashboard.clientPortfolio': 'Cartera de Clientes',
    'dashboard.contactHistory': 'Historial de todos los contactos',
    'dashboard.noLimits': 'Sin Límites',
    'dashboard.projectsMembers': '80 proyectos, 10 miembros',
    'dashboard.freeTrial': '7 días de prueba gratis',
    'dashboard.upgradeNow': 'Actualizar Ahora',

    // Profile
    'profile.title': 'Mi Perfil',
    'profile.photo': 'Foto de Perfil',
    'profile.photoDesc': 'Tu foto se mostrará en tu perfil y proyectos',
    'profile.changePhoto': 'Cambiar Foto',
    'profile.photoFormats': 'JPG, PNG o WEBP. Máximo 5MB.',
    'profile.personalInfo': 'Información Personal',
    'profile.personalInfoDesc': 'Actualiza tus datos de registro',
    'profile.fullName': 'Nombre Completo',
    'profile.email': 'Email',
    'profile.emailChangeNote': 'Al cambiar el email, recibirás un enlace de confirmación',
    'profile.phone': 'Teléfono (con código de área)',
    'profile.birthDate': 'Fecha de Nacimiento',
    'profile.pixConfig': 'Configuración PIX',
    'profile.pixConfigDesc': 'Configura tus datos PIX para recibir pagos a través de la plataforma',
    'profile.pixKeyType': 'Tipo de Clave PIX',
    'profile.selectKeyType': 'Selecciona el tipo de clave',
    'profile.pixKey': 'Clave PIX',
    'profile.pixBeneficiaryName': 'Nombre del Beneficiario',
    'profile.pixBeneficiaryCity': 'Ciudad del Beneficiario',
    'profile.saveChanges': 'Guardar Cambios',
    'profile.saving': 'Guardando...',
    'profile.changePassword': 'Cambiar Contraseña',
    'profile.changePasswordDesc': 'Por tu seguridad, elige una contraseña fuerte',
    'profile.currentPassword': 'Contraseña Actual',
    'profile.newPassword': 'Nueva Contraseña',
    'profile.confirmPassword': 'Confirmar Nueva Contraseña',
    'profile.updatePassword': 'Actualizar Contraseña',
    'profile.updating': 'Actualizando...',
    'profile.subscription': 'Suscripción',
    'profile.manageSub': 'Gestiona tu suscripción y ve los beneficios de tu plan',
    'profile.currentPlan': 'Plan Actual',
    'profile.manageSubscription': 'Gestionar Suscripción',

    // Clients
    'clients.title': 'Cartera de Clientes',
    'clients.export': 'Exportar',
    'clients.contacts': 'Contactos',
    'clients.churnAnalysis': 'Análisis de Churn',
    'clients.searchPlaceholder': 'Buscar por nombre, email o teléfono...',
    'clients.all': 'Todos',
    'clients.clientsLabel': 'Clientes',
    'clients.professionals': 'Profesionales',
    'clients.totalContacts': 'Total de Contactos',
    'clients.noContactFound': 'Ningún contacto encontrado',
    'clients.noContactYet': 'Ningún contacto aún',
    'clients.adjustSearch': 'Intenta ajustar tu búsqueda o filtro',
    'clients.addMembersToStart': 'Añade miembros a tus proyectos para comenzar',
    'clients.firstContact': 'Primer contacto',
    'clients.notProvided': 'No informado',
    'clients.upgradeTitle': 'Cartera de Clientes',
    'clients.upgradeDesc': 'Gestiona todos tus clientes y contactos en un solo lugar. Esta funcionalidad es exclusiva para suscriptores PRO.',

    // Revenue
    'revenue.title': 'Finanzas',
    'revenue.subtitle': 'Gestiona tus ingresos y gastos',
    'revenue.income': 'Ingreso',
    'revenue.expense': 'Gasto',
    'revenue.overview': 'Resumen',
    'revenue.transactions': 'Transacciones',
    'revenue.payments': 'Pagos',
    'revenue.paymentRevenue': 'Facturación de Pagos',
    'revenue.approvedPayments': 'pago(s) aprobado(s)',
    'revenue.noApprovedPayments': 'Ningún pago aprobado aún',
    'revenue.approved': 'Aprobado',
    'revenue.project': 'Proyecto',
    'revenue.paidAt': 'Pagado el',
    'revenue.at': 'a las',
    'revenue.edit': 'Editar',
    'revenue.totalBilling': 'Facturación Total',
    'revenue.completeFinanceManager': 'Gestor de Finanzas Completo',
    'revenue.financeManagerDesc': 'Control de gastos, ingresos manuales, gráficos detallados, resúmenes mensuales y anuales.',
    'revenue.upgradeToPro': 'Actualizar a PRO',
    'revenue.upgradeTitle': 'Facturación y Finanzas',
    'revenue.upgradeDesc': 'Gestiona tus ingresos, gastos y visualiza informes financieros completos. Esta funcionalidad es exclusiva para suscriptores PRO.',

    // Project Details
    'project.back': 'Volver',
    'project.refresh': 'Actualizar',
    'project.payments': 'Pagos',
    'project.archived': 'Archivados',
    'project.members': 'Miembros',
    'project.discussion': 'Discusión',
    'project.logs': 'Logs',
    'project.drawings': 'Dibujos',
    'project.saveTemplate': 'Guardar Plantilla',
    'project.useTemplate': 'Usar Plantilla',
    'project.deleteProject': 'Eliminar Proyecto',
    'project.addTab': 'Nueva Pestaña',
    'project.addTopic': 'Nuevo Tema',
    'project.noTabs': 'Ninguna pestaña creada',
    'project.createFirstTab': 'Crea tu primera pestaña para organizar los temas',
    'project.owner': 'Propietario',
    'project.professional': 'Profesional',
    'project.client': 'Cliente',
    'project.addMember': 'Añadir Miembro',
    'project.loadError': 'Error al cargar proyecto',
    'project.refreshSuccess': 'Actualizado con éxito',
    'project.charges': 'Cobros',
    'project.noTabsYet': 'Ninguna pestaña aún',
    'project.createFirstTabBtn': 'Crear Primera Pestaña',
    'project.topicMoved': '¡Tema movido!',
    'project.movedTo': 'Movido a',
    'project.errorMovingTopic': 'Error al mover tema',
    'project.errorReordering': 'Error al reorganizar',
    'project.couldNotSaveOrder': 'No se pudo guardar el nuevo orden.',
    'project.updateError': 'Error al actualizar',
    'project.couldNotUpdate': 'No se pudo actualizar la información',
    'project.drafts': 'Borradores',
    'projectDetails.tabLimitReached': 'Límite de temas alcanzado',
    'projectDetails.tabLimitReachedDesc': 'Esta pestaña ya alcanzó el límite máximo de temas.',
    'board.tabLimitReached': 'Límite de temas alcanzado',
    'board.tabLimitReachedDesc': 'Esta pestaña ya alcanzó el límite máximo de temas.',

    // Customization
    'customization.title': 'Personalización',
    'customization.upgradeTitle': 'Personalización',
    'customization.upgradeDesc': 'Personaliza tu plataforma con tus colores, logo y wallpaper. Esta funcionalidad es exclusiva para suscriptores PRO.',
    'customization.images': 'Imágenes',
    'customization.imagesDesc': 'Personaliza las imágenes de tu marca',
    'customization.wallpaper': 'Fondo de Pantalla',
    'customization.logo': 'Logo de la Empresa',
    'customization.profilePhoto': 'Foto de Perfil',
    'customization.upload': 'Subir',
    'customization.uploadComplete': 'Subida completada',
    'customization.wallpaperUpdated': 'Wallpaper actualizado con éxito.',
    'customization.logoUpdated': 'Logo actualizado con éxito.',
    'customization.profileUpdated': 'Foto de perfil actualizada con éxito.',
    'customization.fileTooLarge': 'Archivo muy grande',
    'customization.maxSize': 'El archivo debe tener como máximo 5MB.',
    'customization.invalidType': 'Tipo de archivo inválido',
    'customization.onlyImages': 'Solo se permiten imágenes.',
    'customization.recommendedWallpaper': 'Recomendado: 1920x1080px, máximo 5MB',
    'customization.recommendedLogo': 'Recomendado: formato cuadrado (500x500px), fondo transparente, máximo 5MB',
    'customization.recommendedProfile': 'Recomendado: formato cuadrado (400x400px), máximo 5MB',
    'customization.colors': 'Paleta de Colores',
    'customization.colorsDesc': 'Personaliza los colores de tu interfaz',
    'customization.primaryColor': 'Color Primario',
    'customization.secondaryColor': 'Color Secundario',
    'customization.accentColor': 'Color de Acento',
    // MyTasks
    'myTasks.title': 'Mis Tareas',
    'myTasks.subtitle': 'Organiza tus tareas por día',
    'myTasks.total': 'Total',
    'myTasks.pending': 'Pendientes',
    'myTasks.completed': 'Completadas',
    'myTasks.overdue': 'Atrasadas',
    'myTasks.scheduled': 'Agendadas',
    'myTasks.list': 'Lista',
    'myTasks.calendar': 'Calendario',
    'myTasks.sortBy': 'Ordenar por',
    'myTasks.groupBy': 'Agrupar por',
    'myTasks.scheduledDate': 'Fecha Programada',
    'myTasks.deadline': 'Plazo',
    'myTasks.startDate': 'Fecha de Inicio',
    'myTasks.priority': 'Prioridad',
    'myTasks.project': 'Proyecto',
    'myTasks.byDate': 'Por Fecha',
    'myTasks.byProject': 'Por Proyecto',
    'myTasks.byStatus': 'Por Estado',
    'myTasks.noGrouping': 'Sin Agrupación',
    'myTasks.noTasksFound': 'No se encontraron tareas',
    'myTasks.notAssigned': 'No estás asignado a ningún tema.',
    'myTasks.noPending': 'No hay tareas pendientes.',
    'myTasks.noCompleted': 'No hay tareas completadas.',
    'myTasks.topicDeadline': 'Plazo',
    'myTasks.scheduledFor': 'Agendado para',
    'myTasks.unscheduled': 'Tarea desagendada',
    'myTasks.scheduleError': 'Error al agendar',
    'myTasks.couldNotUpdate': 'No se pudo actualizar la fecha.',
    'myTasks.today': 'Hoy',
    'myTasks.dragHere': 'Arrastra tareas aquí',
    'myTasks.notScheduled': 'Sin Agendar',
    'myTasks.unscheduleHere': 'Arrastra tareas aquí para desagendar',
    'myTasks.more': 'más',
    'myTasks.topic': 'Tema',
    'myTasks.tab': 'Pestaña',
    'myTasks.overdueGroup': '⚠️ Atrasadas',
    'myTasks.todayGroup': '📅 Hoy',
    'myTasks.tomorrowGroup': '🌅 Mañana',
    'myTasks.thisWeekGroup': '📆 Esta Semana',
    'myTasks.thisMonthGroup': '📋 Este Mes',
    'myTasks.futureGroup': '🔮 Futuro',
    'myTasks.noDateGroup': '📝 Sin Fecha',
    'myTasks.completedGroup': '✅ Completadas',
    'myTasks.pendingGroup': '⏳ Pendientes',
    'myTasks.week': 'Semana',
    'myTasks.month': 'Mes',
    'myTasks.goToToday': 'Ir a Hoy',
    'myTasks.sun': 'Dom',
    'myTasks.mon': 'Lun',
    'myTasks.tue': 'Mar',
    'myTasks.wed': 'Mié',
    'myTasks.thu': 'Jue',
    'myTasks.fri': 'Vie',
    'myTasks.sat': 'Sáb',
    'myTasks.noProject': 'Sin Proyecto',

    // Schedule
    'schedule.title': 'Agenda',
    'schedule.newEvent': 'Nuevo Evento',
    'schedule.editEvent': 'Editar Evento',
    'schedule.eventTitle': 'Título',
    'schedule.description': 'Descripción',
    'schedule.date': 'Fecha',
    'schedule.allDay': 'Todo el día',
    'schedule.start': 'Inicio',
    'schedule.end': 'Fin',
    'schedule.color': 'Color',
    'schedule.purple': 'Púrpura',
    'schedule.blue': 'Azul',
    'schedule.green': 'Verde',
    'schedule.yellow': 'Amarillo',
    'schedule.red': 'Rojo',
    'schedule.pink': 'Rosa',
    'schedule.save': 'Guardar',
    'schedule.create': 'Crear',
    'schedule.cancel': 'Cancelar',
    'schedule.loadError': 'Error al cargar eventos',
    'schedule.eventUpdated': '¡Evento actualizado!',
    'schedule.eventCreated': '¡Evento creado!',
    'schedule.saveError': 'Error al guardar evento',
    'schedule.eventRemoved': '¡Evento eliminado!',
    'schedule.removeError': 'Error al eliminar evento',
    'schedule.noEvents': 'Sin eventos',
    'schedule.upgradeDesc': 'Administra tu agenda y citas. Esta función está disponible para suscriptores Prime y PRO.',

    // Achievements
    'achievements.title': 'Logros',
    'achievements.trackProgress': 'Sigue tu progreso',
    'achievements.yourStreak': 'Tu Racha',
    'achievements.yourProgress': 'Tu Progreso',
    'achievements.projects': 'Proyectos',
    'achievements.tasks': 'Tareas',
    'achievements.clients': 'Clientes',
    'achievements.payments': 'Pagos',
    'achievements.comments': 'Comentarios',
    'achievements.allAchievements': 'Todos los Logros',

    // Common
    'common.loading': 'Cargando...',
    'common.save': 'Guardar',
    'common.cancel': 'Cancelar',
    'common.delete': 'Eliminar',
    'common.edit': 'Editar',
    'common.create': 'Crear',
    'common.back': 'Volver',
    'common.close': 'Cerrar',
    'common.search': 'Buscar',
    'common.filter': 'Filtrar',
    'common.export': 'Exportar',
    'common.required': 'Obligatorio',

    // Push Notifications
    'pushNotifications': 'Notificaciones Push',
    'pushEnabled': '¡Notificaciones activadas!',
    'pushEnabledDesc': 'Recibirás alertas incluso con la app cerrada.',
    'pushDisabled': 'Notificaciones desactivadas',
    'pushDisabledDesc': 'Ya no recibirás alertas push.',
    'pushEnable': 'Activar Notificaciones',
    'pushDisable': 'Desactivar',
    'pushError': 'Error en notificaciones',
    'pushErrorDesc': 'No fue posible configurar las notificaciones.',
    'pushNotSupported': 'No soportado',
    'pushNotSupportedDesc': 'Tu navegador no soporta notificaciones push.',
    'pushPermissionDenied': 'Permiso denegado',
    'pushPermissionDeniedDesc': 'Puedes cambiarlo en la configuración del navegador.',

    // Dialogs
    'dialog.newProject': 'Nuevo Proyecto',
    'dialog.createProject': 'Crear Nuevo Proyecto',
    'dialog.createProjectDesc': 'Crea un nuevo proyecto para organizar tus tareas y colaborar con tu equipo.',
    'dialog.projectName': 'Nombre del Proyecto',
    'dialog.projectNamePlaceholder': 'Ej: Sitio Web Corporativo',
    'dialog.descriptionOptional': 'Descripción (opcional)',
    'dialog.descriptionPlaceholder': 'Describe los objetivos y alcance del proyecto...',
    'dialog.nameRequired': 'Nombre obligatorio',
    'dialog.enterProjectName': 'Por favor, ingresa un nombre para el proyecto.',
    'dialog.authError': 'Error de autenticación',
    'dialog.needLogin': 'Necesitas estar conectado para crear proyectos.',
    'dialog.projectCreated': '¡Proyecto creado!',
    'dialog.projectCreatedDesc': 'Tu proyecto fue creado con éxito.',
    'dialog.createError': 'Error al crear proyecto',
    'dialog.createProjectBtn': 'Crear Proyecto',
    
    'dialog.editProject': 'Editar Proyecto',
    'dialog.editProjectDesc': 'Cambia el título y la descripción del proyecto.',
    'dialog.projectUpdated': '¡Proyecto actualizado!',
    'dialog.updateError': 'Error al actualizar proyecto',
    
    'dialog.deleteProject': 'Eliminar Proyecto',
    'dialog.deleteWarning': 'Esta acción es irreversible y eliminará permanentemente:',
    'dialog.deleteAllTabs': 'Todas las pestañas del proyecto',
    'dialog.deleteAllTopics': 'Todos los temas y sus descripciones',
    'dialog.deleteAllMembers': 'Todos los miembros asociados',
    'dialog.deleteAllCharges': 'Todas las cobros PIX creados',
    'dialog.typeToConfirm': 'Para confirmar, escribe el nombre del proyecto:',
    'dialog.confirmProjectName': 'Confirmar nombre del proyecto',
    'dialog.typeProjectName': 'Escribe el nombre del proyecto',
    'dialog.incorrectName': 'Nombre incorrecto',
    'dialog.typeExactName': 'Escribe el nombre exacto del proyecto para confirmar la eliminación.',
    'dialog.projectDeleted': 'Proyecto eliminado',
    'dialog.projectDeletedDesc': 'El proyecto y todos sus datos fueron eliminados con éxito.',
    'dialog.deleteError': 'Error al eliminar proyecto',
    'dialog.deleting': 'Eliminando...',
    
    'dialog.leaveProject': 'Salir del Proyecto',
    'dialog.leaveConfirm': '¿Estás seguro de que deseas salir del proyecto',
    'dialog.leaveWarning': 'Perderás acceso a toda la información y ya no podrás colaborar en este proyecto a menos que seas invitado nuevamente.',
    'dialog.leftProject': 'Saliste del proyecto',
    'dialog.leftProjectDesc': 'Ya no eres parte de',
    'dialog.leaveError': 'Error al salir del proyecto',
    'dialog.leaving': 'Saliendo...',
    
    'dialog.addMember': 'Añadir Miembro',
    'dialog.addMemberDesc': 'Añade un miembro al proyecto por email registrado en la plataforma.',
    'dialog.email': 'Email',
    'dialog.emailPlaceholder': 'email@ejemplo.com',
    'dialog.role': 'Rol',
    'dialog.client': 'Cliente',
    'dialog.professional': 'Profesional',
    'dialog.validationError': 'Error de validación',
    'dialog.userNotFound': 'Usuario no encontrado con este email',
    'dialog.alreadyMember': 'Este usuario ya es miembro del proyecto',
    'dialog.memberAdded': '¡Miembro añadido!',
    'dialog.addedAs': 'fue añadido al proyecto como',
    'dialog.addMemberError': 'Error al añadir miembro',
    
    'dialog.newTab': 'Nueva Pestaña',
    'dialog.newTabDesc': 'Crea una nueva pestaña para organizar tus temas.',
    'dialog.tabName': 'Nombre de la Pestaña',
    'dialog.tabNamePlaceholder': 'Ej: Diseño, Desarrollo, Marketing',
    'dialog.tabNameRequired': 'Por favor, ingresa un nombre para la pestaña.',
    'dialog.tabCreated': '¡Pestaña creada!',
    'dialog.tabCreatedDesc': 'La pestaña fue creada con éxito.',
    'dialog.tabError': 'Error al crear pestaña',
    'dialog.createTab': 'Crear Pestaña',
    
    'dialog.newTopic': 'Nuevo Tema',
    'dialog.newTopicDesc': 'Añade un nuevo tema para organizar tus tareas.',
    'dialog.topicName': 'Nombre del Tema',
    'dialog.topicNamePlaceholder': 'Ej: Crear landing page',
    'dialog.topicNameRequired': 'Por favor, ingresa un nombre para el tema.',
    'dialog.description': 'Descripción',
    'dialog.descriptionBrief': 'Breve descripción del tema (opcional)',
    'dialog.content': 'Contenido',
    'dialog.contentPlaceholder': 'Añade notas, información detallada o cualquier contenido relevante...',
    'dialog.color': 'Color',
    'dialog.topicCreated': '¡Tema creado!',
    'dialog.topicCreatedDesc': 'El tema fue creado con éxito.',
    'dialog.topicError': 'Error al crear tema',
    'dialog.createTopic': 'Crear Tema',
    
    'dialog.editTopic': 'Editar Tema',
    'dialog.editTopicDesc': 'Edita el título, descripción, plazo o mueve a otra pestaña.',
    'dialog.title': 'Título',
    'dialog.startDate': 'Fecha de Inicio',
    'dialog.endDate': 'Fecha Final',
    'dialog.start': 'Inicio',
    'dialog.end': 'Final',
    'dialog.clear': 'Limpiar',
    'dialog.responsible': 'Responsable',
    'dialog.selectResponsible': 'Selecciona un responsable',
    'dialog.noResponsible': 'Sin responsable',
    'dialog.moveToTab': 'Mover a pestaña',
    'dialog.current': 'actual',
    'dialog.topicMoved': '¡Tema movido!',
    'dialog.topicMovedTo': 'El tema fue movido a',
    'dialog.topicUpdated': '¡Tema actualizado!',
    'dialog.topicUpdatedDesc': 'Los cambios fueron guardados con éxito.',
    'dialog.topicUpdateError': 'Error al actualizar tema',
    'dialog.moveAndSave': 'Mover y Guardar',
    'dialog.saveChanges': 'Guardar Cambios',
    
    'dialog.archiveTopic': 'Archivar tema',
    'dialog.archiveConfirm': '¿Estás seguro de que deseas archivar',
    'dialog.archiveWarning': 'El tema será eliminado automáticamente después de 30 días, pero puedes restaurarlo antes.',
    'dialog.topicArchived': 'Tema archivado',
    'dialog.topicArchivedDesc': 'El tema fue archivado y será eliminado después de 30 días.',
    'dialog.archiveError': 'Error al archivar tema',
    'dialog.archive': 'Archivar',
    'dialog.sendNotification': 'Enviar Notificación',
    'dialog.sendNotificationDesc': 'Envía una notificación a un miembro del proyecto. Aparecerá en su dashboard por 24 horas.',
    'dialog.selectMember': 'Seleccionar miembro',
    'dialog.allMembers': 'todos los miembros',
    'dialog.selectMemberPlaceholder': 'Elige un miembro',
    'dialog.message': 'Mensaje',
    'dialog.messagePlaceholder': 'Escribe tu mensaje...',
    'dialog.notificationSent': 'Notificación enviada',
    'dialog.notificationSentDesc': 'La notificación fue enviada con éxito.',
    'dialog.notificationSentAllDesc': 'Notificación enviada a todos los miembros.',
    
    'dialog.upgradePlan': 'Actualiza tu plan',
    'dialog.limitProjects': 'Has alcanzado el límite de proyectos de tu plan.',
    'dialog.limitMembers': 'Has alcanzado el límite de miembros para este proyecto.',
    'dialog.limitTabs': 'Has alcanzado el límite de pestañas para este proyecto.',
    'dialog.limitTopics': 'Has alcanzado el límite de temas para esta pestaña.',
    'dialog.limitDefault': 'Has alcanzado un límite de tu plan.',
    'dialog.upTo': 'Hasta',
    'dialog.projects': 'proyectos',
    'dialog.membersPerProject': 'miembros por proyecto',
    'dialog.basicExport': 'Exportación básica',
    'dialog.advancedCustomization': 'Personalización avanzada',
    'dialog.reportsAnalytics': 'Informes y analytics',
    'dialog.prioritySupport': 'Soporte prioritario',
    'dialog.advancedExport': 'Exportación avanzada',
    'dialog.subscribePrime': 'Suscribir Prime',
    'dialog.subscribePro': 'Suscribir PRO',
    'dialog.upgradeToPro': 'Actualizar a PRO',
    'dialog.recommended': 'Recomendado',
    'dialog.processing': 'Procesando...',
    'dialog.youHave': 'tienes',
    
    // ProjectDetails
    'projectDetails.errorLoading': 'Error al cargar proyecto',
    'projectDetails.updated': 'Actualizado con éxito',
    'projectDetails.updatedDesc': 'Toda la información ha sido actualizada',
    'projectDetails.updateError': 'Error al actualizar',
    'projectDetails.updateErrorDesc': 'No se pudo actualizar la información',
    'projectDetails.topicMoved': '¡Tema movido!',
    'projectDetails.movedTo': 'Movido a',
    'projectDetails.errorMoving': 'Error al mover tema',
    'projectDetails.errorReorder': 'Error al reordenar',
    'projectDetails.errorReorderDesc': 'No se pudo guardar el nuevo orden.',
    'projectDetails.refresh': 'Actualizar',
    'projectDetails.saveAsTemplate': 'Guardar como Plantilla',
    'projectDetails.template': 'Plantilla',
    'projectDetails.useTemplate': 'Usar Plantilla',
    'projectDetails.deleteProject': 'Eliminar Proyecto',
    'projectDetails.delete': 'Eliminar',
    'projectDetails.charges': 'Cobros',
    'projectDetails.members': 'Miembros',
    'projectDetails.archived': 'Archivados',
    'projectDetails.drawings': 'Bocetos',
    'projectDetails.discussion': 'Discusión',
    'projectDetails.logs': 'Registros',
    'projectDetails.newTab': 'Nueva Pestaña',
    'projectDetails.tabs': 'Pestañas',
    'projectDetails.limit': 'Límite',
    'projectDetails.noTabsYet': 'Sin pestañas aún',
    'projectDetails.noTabsDesc': 'Crea tu primera pestaña para comenzar a organizar los temas del proyecto.',
    'projectDetails.createFirstTab': 'Crear Primera Pestaña',
    'projectDetails.projectMembers': 'Miembros del Proyecto',
    'projectDetails.addMember': 'Agregar Miembro',
    'projectDetails.projectDiscussion': 'Discusión del Proyecto',
    
    // Board/Topic Components
    'board.removePriority': 'Quitar prioridad',
    'board.markAsPriority': 'Marcar como prioridad',
    'board.settings': 'Configuraciones',
    'board.archive': 'Archivar',
    'board.edit': 'Editar',
    'board.deadline': 'Plazo',
    'board.delete': 'Eliminar',
    'board.dropHere': 'Suelta aquí',
    'board.noTopics': 'Sin temas',
    'board.add': 'Agregar',
    'board.topicMoved': '¡Tema movido!',
    'board.movedTo': 'Movido a',
    'board.errorMoving': 'Error al mover',
    'board.errorUpdatingTopic': 'Error al actualizar tema',
    'board.errorUpdatingPriority': 'Error al actualizar prioridad',
    
    // Members List
    'members.roleUpdated': 'Cargo actualizado',
    'members.roleUpdatedDesc': 'El cargo del miembro ha sido cambiado exitosamente.',
    'members.errorUpdatingRole': 'Error al actualizar cargo',
    'members.memberRemoved': 'Miembro eliminado',
    'members.memberRemovedDesc': 'El miembro ha sido eliminado del proyecto.',
    'members.errorRemoving': 'Error al eliminar miembro',
    'members.professional': 'Profesional',
    'members.client': 'Cliente',
    'members.addedOn': 'Agregado el',
    'members.noMembers': 'Ningún miembro agregado aún.',
    'members.removeMember': 'Eliminar miembro',
    'members.removeConfirm': '¿Estás seguro de que deseas eliminar a',
    'members.removeWarning': 'del proyecto? Esta acción no se puede deshacer.',
    'members.remove': 'Eliminar',

    // Subscription Page
    'subscription.choosePlan': 'Elige tu plan',
    'subscription.unlockPotential': 'Desbloquea todo el potencial de Taskeer',
    'subscription.currentPlan': 'Plan actual',
    'subscription.recommended': 'Recomendado',
    'subscription.daysFree': 'días gratis',
    'subscription.yourPlan': 'Tu Plan',
    'subscription.month': 'mes',
    'subscription.freeTrial': 'Prueba gratis de 7 días',
    'subscription.currentPlanBtn': 'Plan Actual',
    'subscription.manage': 'Gestionar',
    'subscription.downgrade': 'Cambiar a menor',
    'subscription.basicPlan': 'Plan Básico',
    'subscription.processing': 'Procesando...',
    'subscription.startTrial': 'Iniciar prueba de {days} días',
    'subscription.subscribeNow': 'Suscribirse Ahora',
    'subscription.upgrade': 'Mejorar Plan',
    'subscription.startingFrom': 'Desde',
    'subscription.upgradeToPrime': 'Mejorar Plan',
    'subscription.back': 'Volver',
    'subscription.primeOrPro': 'Taskeer Prime o PRO',
    'subscription.exclusiveFeatures': 'Acceso a funcionalidades exclusivas',
    'subscription.trialInfo': 'El plan PRO incluye 7 días de prueba gratis. Después del período de prueba, se cobrará automáticamente o puedes cancelar en cualquier momento.',
    'subscription.backToDashboard': 'Volver al Dashboard',
    'subscription.free': 'Gratuito',
    'subscription.feature1Project': '1 proyecto',
    'subscription.feature1Member': '1 miembro por proyecto',
    'subscription.feature3Tabs': '3 pestañas por proyecto',
    'subscription.feature5Topics': '5 temas por pestaña',
    'subscription.featureBasic': 'Funciones básicas',
    'subscription.feature10Projects': 'Hasta 10 proyectos',
    'subscription.feature3Members': 'Hasta 3 miembros por proyecto',
    'subscription.feature20Tabs': '20 pestañas por proyecto',
    'subscription.feature30Topics': '30 temas por pestaña',
    'subscription.featureExportCSV': 'Exportación básica (CSV)',
    'subscription.featureEmailSupport': 'Soporte por email',
    'subscription.feature80Projects': 'Hasta 80 proyectos',
    'subscription.feature10Members': 'Hasta 10 miembros por proyecto',
    'subscription.featureCustomization': 'Personalización avanzada',
    'subscription.featureAnalytics': 'Informes y analytics',
    'subscription.featureClients': 'Cartera de clientes',
    'subscription.featureExportAdvanced': 'Exportación avanzada (Excel, PDF)',
  },
};

const detectBrowserLanguage = (): Language => {
  const browserLang = navigator.language || (navigator as any).userLanguage || 'pt';
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  if (langCode === 'en') return 'en';
  if (langCode === 'es') return 'es';
  return 'pt'; // Default to Portuguese
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const saved = localStorage.getItem('taskeer-language');
    if (saved) return saved as Language;
    return detectBrowserLanguage();
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('taskeer-language', lang);
  };

  const t = (key: string, params?: InterpolationParams): string => {
    let text = translations[language][key] || key;
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      });
    }
    return text;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
